import * as Spacekit from 'spacekit.js'
import PopupService from '@/services/popup.service'
import VisualisationService from '@/services/visualisation.service'
import HUDService from '@/services/hud.service'
import Keys from '@/constants/keys'
import * as THREE from 'three'
import UtilsService from '@/services/utils.service'
import SynodicCalculationService from '@/services/synodic-calculation.service'
import MemoryService from '@/services/memory.service'
import TimeFrameService from '@/services/timeframe.service'
import moment from "moment"
import Config from "@/constants/config"
import { Line2 } from 'three/examples/jsm/lines/Line2';
import { LineGeometry } from 'three/examples/jsm/lines/LineGeometry';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial';
import SyntheticObjectsService from "@/services/synthetic-objects.service";
import LayerService from "@/services/layer.service";

const SPHERE_SCALE_FACTOR = 0.002; // Scale factor for the sphere size in the particle system
const PLANET_ORBIT_SAMPLE_POINTS = 16384;
let worldPos = new THREE.Vector3();
const MIN_SCALE = 1e-5;
const MAX_SCALE = 1e2;
const STARS_DPR_PATCH_RETRIES = 40;
const STARS_DPR_PATCH_RETRY_MS = 100;
const STARS_DPR_MAX_SCALE = 2;
const STARS_ROTATION_PATCH_RETRIES = 30;
const STARS_ROTATION_PATCH_RETRY_MS = 100;

const planetMarkerColors = {
    mercury: 0xffea8e,
    venus: 0xf4ae25,
    earth: 0x1878bd,
    mars: 0xf44b25,
    jupiter: 0x9c2824,
    saturn: 0x9c6124,
    uranus: 0x3d37ad,
    neptune: 0x4ea7e7,
};

const SpacekitService = {
    COLOR_PERTURBED: 0xffc1c1,
    COLOR_PERTURBED_SELECTED: 0xFF6363,
    COLOR_KEPLERIAN: 0x9fcef1,
    COLOR_KEPLERIAN_SELECTED: 0x0F84DD,
    COLOR_SUN: 0xFFF000,
    COLOR_MOON: 0x666666,
    COLOR_CLOSEST_POINT: 0xB50000,
    COLOR_ORBIT_RANGE: 0xFDDA0D,
    COLOR_SYNODIC_ORBIT: 0x5998D4,
    VIZ_UNITS: 10000,
    ORBIT_TYPE: {
        UNKNOWN: 0,
        PARABOLIC: 1,
        HYPERBOLIC: 2,
        ELLIPTICAL: 3,
        TABLE: 4
    },
    AU: 149597871,
    mu_Sun: 1.327e20, // Gravitational parameter of the Sun (m^3/s^2)
    USER_DEFINED_OBJECT_DEFAULT_DIAMETER: 250,
    USER_DEFINED_OBJECT_FVT_DEFAULT_DIAMETER: 20,
    USER_DEFINED_OBJECT_DISTANCE_IMPACT_LIMIT: 300000,
    _starsDprChangeHandler: null,
    _starsLastDpr: null,

    buildSimulation(tool) {
        if (document.querySelector('.vt__simulation')){
            return new Spacekit.Simulation(document.querySelector('.vt__simulation'), {
                basePath: '/static/spacekit',
                particleTextureUrl: '/static/textures/particle.png',
                maxNumParticles: 2**18,
                unitsPerAu: tool === 'fvt' ? SpacekitService.VIZ_UNITS : 1,
                camera: {
                    enableDrift: false,
                },
            });
    }else {
        return;
    }
    },

    buildBackgroundObjects(tool) {
        const vt = window['vt'];        
        const camera = vt.getViewer().cameraControls;
        const scene = vt.getScene();
        const settings = VisualisationService.getSettings();
        const backColor = settings.objectsSettings.background.color;

        scene.background = new THREE.Color(backColor);
        scene.background.set( backColor );

        if (settings.objectsSettings.dsos.value && tool !== 'sovt') {
            vt.stars = vt.createStars();
            SpacekitService.applyStarsDevicePixelRatioPatch(vt);

        }

        if (tool === 'fvt'){
            SpacekitService.applyFvtStarsRotationPatch(vt);
        }

        if (settings.objectsSettings.milkyWayBackground.value && tool !== 'sovt') {
            //vt.skybox = vt.createSkybox(Spacekit.SkyboxPresets.NASA_TYCHO); - old background
            
            vt.skybox = vt.createSkybox({
                //https://www.esa.int/var/esa/storage/images/esa_multimedia/images/2020/12/the_colour_of_the_sky_from_gaia_s_early_data_release_32/22358131-1-eng-GB/The_colour_of_the_sky_from_Gaia_s_Early_Data_Release_3_pillars.png
                textureUrl: '/static/textures/MilkyWay.png',
            });
            
            if (tool === 'fvt')
                vt.skybox.mesh.rotation.x = (23.5 + 7.8) * Math.PI / 180;
            else 
                vt.skybox.mesh.rotation.x = 7.8 * Math.PI / 180;

            vt.skybox.mesh.rotation.y = 29.55 * Math.PI / 180;
            vt.skybox.mesh.rotation.z = 85.55 * Math.PI / 180;
            SpacekitService.applySkyboxBackgroundDepthPatch(vt);
        }

        let size = 80;
        let divisions = 80;
        if (tool === 'ovt') {
            SpacekitService.toggleEarthName(true);
        }
        if (tool === 'fvt') {

            const activeObject = VisualisationService.getOrbitActiveObject();
            if (activeObject) {
                let name = activeObject._options.name;
                if (name.includes(" ")) {
                    name = name.split(" ")[0];
                }                
                //VisualisationService.checkObject(name, false);
            }
    
            size = 120;
            divisions = 18;
        }
        const gridHelper = new THREE.GridHelper(size, divisions, 0x666666, 0x333333);
        const axesHelper = new THREE.AxesHelper(2);

        const axesColors = axesHelper.geometry.attributes.color;
        axesColors.setXYZ(0, 1, 0, 0);
        axesColors.setXYZ(1, 1, 0, 0);
        axesColors.setXYZ(2, 0, 1, 0);
        axesColors.setXYZ(3, 0, 1, 0);
        axesColors.setXYZ(4, 0, 0.4, 1);
        axesColors.setXYZ(5, 0, 0.4, 1);

        gridHelper.geometry.rotateX(Math.PI / 2);
        if (!settings.objectsSettings.horizontalGrid.value) {
            gridHelper.visible = false;
        }
        if (!settings.objectsSettings.axisGuide.value) {
            axesHelper.visible = false;
        }
        scene.add(gridHelper);
        scene.add(axesHelper);

        vt.flybyScene = [];
        vt.planets = [];
        vt.planetMarkers = {};

        if (tool === 'ovt') {
            vt.createObject('sun', Spacekit.SpaceObjectPresets.SUN);

            const mercury = vt.createSphere('mercury', {...Spacekit.SpaceObjectPresets.MERCURY, ...{name: 'Mercury', textureUrl: '/static/textures/detailed/mercury.jpg', radius: 0.0024394, rotation: { enable: true, period: 58.646, lambdaDeg: 252.2509,}, orbitPathSettings: { numberSamplePoints: PLANET_ORBIT_SAMPLE_POINTS } }});
            const venus = vt.createSphere('venus', {...Spacekit.SpaceObjectPresets.VENUS, ...{name: 'Venus', textureUrl: '/static/textures/detailed/venus.jpg', radius: 0.0060518, rotation: { enable: true, period: -243.018, lambdaDeg: 181.9798,}, orbitPathSettings: { numberSamplePoints: PLANET_ORBIT_SAMPLE_POINTS } }});
            const earth = vt.createSphere('earth', {...Spacekit.SpaceObjectPresets.EARTH, ...{name: 'Earth', textureUrl: '/static/textures/detailed/earth.jpg', radius: 0.0063710084, rotation: { enable: true, period: 0.9972696322627929, lambdaDeg: 100.4664,}, orbitPathSettings: { numberSamplePoints: PLANET_ORBIT_SAMPLE_POINTS } }});
            const mars = vt.createSphere('mars', {...Spacekit.SpaceObjectPresets.MARS, ...{name: 'Mars', textureUrl: '/static/textures/detailed/mars.jpg', radius: 0.0033895, rotation: { enable: true, period: 1.026, lambdaDeg: 355.4329,}, orbitPathSettings: { numberSamplePoints: PLANET_ORBIT_SAMPLE_POINTS } }});
            const jupiter = vt.createSphere('jupiter', {...Spacekit.SpaceObjectPresets.JUPITER, ...{name: 'Jupiter', textureUrl: '/static/textures/detailed/jupiter.jpg', radius: 0.069911, rotation: { enable: true, period: 0.41354, lambdaDeg: 34.3515,}, orbitPathSettings: { numberSamplePoints: PLANET_ORBIT_SAMPLE_POINTS } }});
            const saturn = vt.createSphere('saturn', {...Spacekit.SpaceObjectPresets.SATURN, ...{name: 'Saturn', textureUrl: '/static/textures/detailed/saturn.jpg', radius: 0.058232, rotation: { enable: true, period: 0.444, lambdaDeg: 50.0774,}, orbitPathSettings: { numberSamplePoints: PLANET_ORBIT_SAMPLE_POINTS } }});
            const uranus = vt.createSphere('uranus', {...Spacekit.SpaceObjectPresets.URANUS, ...{name: 'Uranus', textureUrl: '/static/textures/detailed/uranus.jpg', radius: 0.025362, rotation: { enable: true, period: -0.718, lambdaDeg: 314.055,}, orbitPathSettings: { numberSamplePoints: PLANET_ORBIT_SAMPLE_POINTS } }});
            const neptune = vt.createSphere('neptune', {...Spacekit.SpaceObjectPresets.NEPTUNE, ...{name: 'Neptune', textureUrl: '/static/textures/detailed/neptune.jpg', radius: 0.024622, rotation: { enable: true, period: 0.671, lambdaDeg: 304.3486,}, orbitPathSettings: { numberSamplePoints: PLANET_ORBIT_SAMPLE_POINTS } }});

            vt.planets = [mercury, venus, earth, mars, jupiter, saturn, uranus, neptune];
            vt.planets.forEach(planet => SpacekitService.rebuildPlanetOrbitPath(planet));

            for (const planet of vt.planets) {
                planet._object3js.name = planet._id.toString();
 
                if (planet._id === 'earth') {
                    planet._options.labelLayer = LayerService.getToolLayers().EARTH_NAME;
                    planet._objectIsRotatable = false;
                    planet._options.rotation.enable = false;
                    HUDService.createLabel(planet, UtilsService.hexColor(settings.objectsSettings.earthName.color));
                } else {
                    planet._options.labelLayer = LayerService.getToolLayers().PLANET_NAMES;
                    HUDService.createLabel(planet,UtilsService.hexColor(settings.objectsSettings.planetsNames.color));
                }
            
            }

            vt.planetMarkers = {};

            for (const planet of vt.planets) {
                const color = planetMarkerColors[planet._id.toLowerCase()];
                const geometry = new THREE.SphereGeometry(0.03);
                
                const material = new THREE.MeshBasicMaterial({
                    color,
                    transparent: true,
                    opacity: 1.0,
                    depthTest: false, // Always show on top of orbits
                    depthWrite: false,
                });
                

                const marker = new THREE.Mesh(geometry, material);
                marker.name = `marker_${planet._id}`;
                vt.getScene().add(marker);
                vt.planetMarkers[planet._id] = marker;
            }

            this.updatePlanetsOrbitsColor(VisualisationService.getSettings().objectsSettings.planetsOrbits.color);
            this.updateEarthOrbitColor(VisualisationService.getSettings().objectsSettings.earthOrbit.color);

        } else if (tool !== 'sovt') {
            vt.createSphere('dummy', {...Spacekit.SpaceObjectPresets.SUN, ...{textureUrl: '/', radius: 0, visible: false, ecliptic: {displayLines: false}}});
        }

        if (tool === 'sovt') {
            vt.createObject('sun', Spacekit.SpaceObjectPresets.SUN);
            SpacekitService.enableSovtToolFeatures();
        }

        camera.enableZoom = false;
        camera.rotateSpeed = 0.25;
        camera.dampingFactor = 0.33;

        vt.onTick = () => {
            if (!vt.isPaused) {
                const now = vt.getDate();
                VisualisationService.setNow(now);
            } 
            SpacekitService.updatePlanetMarkers();
        };
    },

    updatePlanetMarkers() {
        // Get references to the visualizer and camera
        const vt = window['vt'];
        const camera = vt.getViewer().camera;

        // Astronomical unit conversion constants
        const AU_KM = 149597870.7; // 1 AU in kilometers
        const KM_TO_AU = 1 / AU_KM; // Convert km to AU

        const MIN_PIXELS_FOR_PLANET = 0.05;  // when to show the real planet

        // Calculate screen-space conversions
        const screenHeight = window.innerHeight;
        const fovRad = camera.fov * (Math.PI / 180); // Convert FOV to radians

        // Keep markers readable at any zoom
        const MIN_MARKER_PX = 8;   // try 8–16
        const MAX_MARKER_PX = 20;   // cap so it doesn’t look huge up close

        // Planet diameters in kilometers (for size calculations)
        const planetDiameters = {
            mercury: 4879, venus: 12104, earth: 12742, mars: 6779,
            jupiter: 139820, saturn: 116460, uranus: 50724, neptune: 49244,
        };

        const pixelsPerUnit = screenHeight / (2 * Math.tan(fovRad / 2)); // Pixels per 3D unit

        // --- Delay the planet→marker switch for rocky planets ---
        const ROCKY = new Set(['mercury','venus','earth','mars']);  // MODIFIED
        const ROCKY_SWITCH_FACTOR = 0.5; // lower threshold = keeps planet visible longer
        // ------------------------------------------------------------------

        for (const planet of vt.planets) {
            if (!planet) continue;

            const marker = vt.planetMarkers?.[planet._id];
            if (!marker || !planet.get3jsObjects) continue;

            const planetObject = planet.get3jsObjects()?.[0];
            if (!planetObject) continue;

            // one-time safety for visibility conflicts
            marker.frustumCulled = false;
            if (marker.material) {
                marker.material.depthTest = false;  // stay on top of orbits
                marker.material.depthWrite = false;
            }
            marker.renderOrder = 9999;

            // Calculate distance from camera
            const pos = planetObject.position;
            const distAU = pos.distanceTo(camera.position);

            // Determine a normalized base id (e.g. "earth_transformed" -> "earth")
            const id = (planet._id || '').toLowerCase();
            const baseId = id.replace('_transformed', '').replace('_perturbed', '');
            // Get planet size and convert to AU
            const diamKM = planetDiameters[id] || 10000;
            let diamAU = diamKM * KM_TO_AU;

            // If “Earth to scale” is ON, use the scaled diameter so we switch to the marker right as the mesh goes sub-pixel
            const settings = VisualisationService.getSettings?.();
            const earthToScale = settings?.objectsSettings?.earthOnScale?.value === true;

            if (baseId === 'earth' && earthToScale) {
                diamAU *= SynodicCalculationService.EARTH_ON_SCALE_FACTOR; // ~0.00669
            }
            // projected size of the actual planet
            const screenPixelSize = (diamAU / distAU) * pixelsPerUnit;

            // --- Use a lower threshold for rocky planets so they switch later ---
            const effectiveThreshold = ROCKY.has(id)
                ? MIN_PIXELS_FOR_PLANET * ROCKY_SWITCH_FACTOR
                : MIN_PIXELS_FOR_PLANET;
            // --------------------------------------------------------------------------------

            // Visibility logic:
            // - Show actual planet if it's large enough on screen
            // - Show marker if planet is too small but not invisible
            const showPlanet = screenPixelSize >= effectiveThreshold;
            const showMarker = !showPlanet;  // <-- remove 0.001 cutoff

            planetObject.visible = showPlanet;
            marker.visible = showMarker;
            if (!showMarker) continue;

            // keep marker at the planet position
            marker.position.copy(pos);

            // constant-pixel sizing
            const worldPerPixel = (2 * Math.tan(fovRad / 2) * distAU) / screenHeight;

            // base radius of the marker geometry (fallback to 1 if unknown)
            const baseRadius =
                (marker.geometry?.parameters?.radius != null)
                ? marker.geometry.parameters.radius
                : 1;

            // choose target pixel diameter within bounds
            let targetPx = MIN_MARKER_PX;
            // (optional) scale bigger planets a bit more even at far zoom:
            // targetPx += Math.min(6, Math.cbrt(diamKM) / 30);

            targetPx = Math.max(MIN_MARKER_PX, Math.min(targetPx, MAX_MARKER_PX));

            const neededWorldDiameter = targetPx * worldPerPixel;
            const scale = (neededWorldDiameter / 2) / baseRadius;

            marker.scale.set(scale, scale, scale);
        }
    },

    applyStarsDevicePixelRatioPatch(vt, retriesLeft = STARS_DPR_PATCH_RETRIES) {
        if (!vt || !vt.stars) {
            return;
        }

        const starPoints = vt.stars._stars || (vt.stars.get3jsObjects && vt.stars.get3jsObjects()[0]);
        const sizeAttr = starPoints && starPoints.geometry && starPoints.geometry.getAttribute('size');

        if (!starPoints || !sizeAttr || !sizeAttr.array) {
            if (retriesLeft > 0) {
                setTimeout(() => SpacekitService.applyStarsDevicePixelRatioPatch(vt, retriesLeft - 1), STARS_DPR_PATCH_RETRY_MS);
            }
            return;
        }

        const dpr = Math.max(1, Math.min(window.devicePixelRatio || 1, STARS_DPR_MAX_SCALE));
        starPoints.userData = starPoints.userData || {};

        if (!starPoints.userData.starBaseSizes || starPoints.userData.starBaseSizes.length !== sizeAttr.array.length) {
            starPoints.userData.starBaseSizes = Float32Array.from(sizeAttr.array);
        }

        if (starPoints.userData.starSizeDprScale === dpr) {
            return;
        }

        const baseSizes = starPoints.userData.starBaseSizes;
        for (let i = 0; i < sizeAttr.array.length; i++) {
            sizeAttr.array[i] = baseSizes[i] * dpr;
        }
        sizeAttr.needsUpdate = true;
        starPoints.userData.starSizeDprScale = dpr;
    },

    // Watch for browser/window moves that change devicePixelRatio, then reapply the star-size patch.
    registerStarsDevicePixelRatioListener() {
        SpacekitService.unregisterStarsDevicePixelRatioListener();
        SpacekitService._starsLastDpr = window.devicePixelRatio || 1;
        SpacekitService._starsDprChangeHandler = () => {
            const currentDpr = window.devicePixelRatio || 1;
            if (currentDpr === SpacekitService._starsLastDpr) {
                return;
            }
            SpacekitService._starsLastDpr = currentDpr;
            SpacekitService.applyStarsDevicePixelRatioPatch(window['vt']);
        };
        window.addEventListener('resize', SpacekitService._starsDprChangeHandler);
    },

    // Remove the window listener when the simulation is destroyed so old instances stop reacting to screen changes.
    unregisterStarsDevicePixelRatioListener() {
        if (SpacekitService._starsDprChangeHandler) {
            window.removeEventListener('resize', SpacekitService._starsDprChangeHandler);
            SpacekitService._starsDprChangeHandler = null;
        }
        SpacekitService._starsLastDpr = null;
    },

    applyFvtStarsRotationPatch(vt, retriesLeft = STARS_ROTATION_PATCH_RETRIES) {
        if (!vt || !vt.stars) {
            return;
        }

        const starsObject = vt.stars._stars || (vt.stars.get3jsObjects && vt.stars.get3jsObjects()[0]);
        if (!starsObject) {
            if (retriesLeft > 0) {
                setTimeout(() => SpacekitService.applyFvtStarsRotationPatch(vt, retriesLeft - 1), STARS_ROTATION_PATCH_RETRY_MS);
            }
            return;
        }

        starsObject.rotation.x = 23.5 * Math.PI / 180;
    },

    applySkyboxBackgroundDepthPatch(vt) {
        if (!vt || !vt.skybox || !vt.skybox.mesh || !vt.skybox.mesh.material) {
            return;
        }

        const skybox = vt.skybox.mesh;
        const material = skybox.material;

        material.depthTest = false;
        material.depthWrite = false;
        material.needsUpdate = true;

        // Keep skybox strictly in the background so stars render consistently on top.
        skybox.renderOrder = -1000;
    },

    checkCameraFrustum(camera, position) {
        const frustum = new THREE.Frustum();
        const matrix = new THREE.Matrix4().multiplyMatrices(camera.projectionMatrix, camera.matrixWorldInverse);
        frustum.setFromProjectionMatrix(matrix);
        return frustum.containsPoint(position);
    },

    mapEphemeridesTableForFlyby(jds, positions) {
        const dataObject = [];
        for (const [index, jd] of jds.entries()) {
            if (positions[index]) {
                dataObject.push(
                    [jd*1 + 2400000.5, positions[index][0] * SpacekitService.AU, positions[index][1] * SpacekitService.AU, positions[index][2] * SpacekitService.AU, 0, 0, 0]
                )
            }
        }
        const ephemeridesTable = {
            data: dataObject,
            distanceUnits: 'km',
            timeUnits: 'day',
        }
        return new Spacekit.EphemerisTable(ephemeridesTable)
    },

    mapEphemeridesTable(data) {
        const PLANETS = {
            mercury: { soi: 0.000782, orbit: 0.39 },
            venus: { soi: 0.0041177, orbit: 0.72 },
            earth: { soi: 0.00770863, orbit: 1.00 }, // 3 LD = 3 * 384400 / 149597870.7;
            mars: { soi: 0.0038637, orbit: 1.52 },
            jupiter: { soi: 0.322197, orbit: 5.20 },
            saturn: { soi: 0.3643099, orbit: 9.54 },
            uranus: { soi: 0.34693, orbit: 19.2 },
            neptune: { soi: 0.57621141, orbit: 30.06 }
        };

        // Prepare ephemerides table for animation
        const dataObject = data.map(e => [
            e.epoch * 1 + 2400000.5, // MJD to JD
            e.xa * SpacekitService.AU,
            e.ya * SpacekitService.AU,
            e.za * SpacekitService.AU,
            0, 0, 0
        ]);

        const ephemeridesTable = {
            data: dataObject,
            distanceUnits: 'km',
            timeUnits: 'day'
        };

        const uncTable = [];
        const rawCloseApproachTable = [];

        for (const datastamp of data) {
            if (datastamp.unc) {
                uncTable.push(datastamp.epoch);
            }

            if (datastamp.epoch % 1 !== 0) {
                const epoch = parseInt(datastamp.epoch);
                if (!rawCloseApproachTable.some(e => e.epoch === epoch)) {
                    const jd = epoch + 2400000.5;

                    const objPos = {
                        x: datastamp.xa,
                        y: datastamp.ya,
                        z: datastamp.za
                    };

                    // 1. Determine heliocentric distance
                    const heliocentricDistance = Math.sqrt(
                        objPos.x ** 2 + objPos.y ** 2 + objPos.z ** 2
                    );

                    // 2. Identify closest planet by orbit
                    let minDiff = Infinity;
                    let closestPlanet = null;
                    for (const [planet, { orbit }] of Object.entries(PLANETS)) {
                        const diff = Math.abs(heliocentricDistance - orbit);
                        if (diff < minDiff) {
                            minDiff = diff;
                            closestPlanet = planet;
                        }
                    }

                    // 3. Compute asteroid-planet distance
                    const planetEphem = Spacekit.SpaceObjectPresets[closestPlanet.toUpperCase()].ephem;
                    const planetPos = SpacekitService.getPositionAtTimeElliptical(jd, planetEphem);

                    const dx = objPos.x - planetPos.x;
                    const dy = objPos.y - planetPos.y;
                    const dz = objPos.z - planetPos.z;
                    const distanceToPlanet = Math.sqrt(dx ** 2 + dy ** 2 + dz ** 2); // in AU

                    rawCloseApproachTable.push({
                        epoch,
                        distanceToPlanet,
                        planet: closestPlanet
                    });
                }
            }
        }

        rawCloseApproachTable.sort((a, b) => a.epoch - b.epoch);

        // Group sequential epochs
        const groupedEpochs = [];
        let currentGroup = [];

        for (let i = 0; i < rawCloseApproachTable.length; i++) {
            const current = rawCloseApproachTable[i];
            const previous = rawCloseApproachTable[i - 1];

            if (i === 0 || current.epoch === previous.epoch + 1) {
                currentGroup.push(current);
            } else {
                groupedEpochs.push(currentGroup);
                currentGroup = [current];
            }
        }
        if (currentGroup.length > 0) {
            groupedEpochs.push(currentGroup);
        }

        // Final output tables
        const closeApproachTable = [];
        const detectedCloseApproachPerPlanet = {};
        for (const planet of Object.keys(PLANETS)) {
            detectedCloseApproachPerPlanet[planet] = [];
        }

        for (const group of groupedEpochs) {
            const planet = group[0].planet; // assume same planet per group
            const minDistance = Math.min(...group.map(e => e.distanceToPlanet));
            const isClose = minDistance <= PLANETS[planet].soi;

            for (const entry of group) {
                closeApproachTable.push(entry.epoch);
                for (const p of Object.keys(PLANETS)) {
                    detectedCloseApproachPerPlanet[p].push(p === planet ? isClose : false);
                }
            }
        }

        return {
            ephemObject: new Spacekit.EphemerisTable(ephemeridesTable),
            uncTable,
            closeApproachTable,
            ...Object.fromEntries(
                Object.entries(detectedCloseApproachPerPlanet).map(
                    ([planet, flags]) => [`detectedCloseApproachForFVT_${planet}`, flags]
                )
            )
        };
    },

    getPositionAtTimeElliptical(jd, eph){
        // Eccentricity
        const e = eph.get('e');
    
        // Mean anomaly
        const ma = eph.get('ma', 'rad');
    
        // Calculate mean anomaly at jd
        const n = eph.get('n', 'rad');
        const epoch = eph.get('epoch');
        const d = jd - epoch;

        const M = ma + n * d;

        // Estimate eccentric and true anom using iterative approx
        let E0 = M;
        for (let count = 0; count < 100; count++) {
          const E1 = M + e * Math.sin(E0);
          const lastdiff = Math.abs(E1 - E0);
          E0 = E1;
    
          if (lastdiff < 0.0000001) {
            break;
          }
        }

        const E = E0;
        const v = 2 * Math.atan(Math.sqrt((1 + e) / (1 - e)) * Math.tan(E / 2));

        // Radius vector, in AU
        const a = eph.get('a');
        const r = (a * (1 - e * e)) / (1 + e * Math.cos(v));
    
        return this.vectorToHeliocentric(v, r, eph);
    },

    vectorToHeliocentric(v, r, eph) {
        
        // Inclination, Longitude of ascending node, Longitude of perihelion
        const i = eph.get('i');
        const o = eph.get('om');
        const p = eph.get('wBar');
    
        // Heliocentric coords
        const X = r * (Math.cos(o) * Math.cos(v + p - o) - Math.sin(o) * Math.sin(v + p - o) * Math.cos(i));
        const Y = r * (Math.sin(o) * Math.cos(v + p - o) + Math.cos(o) * Math.sin(v + p - o) * Math.cos(i));
        const Z = r * (Math.sin(v + p - o) * Math.sin(i));
    
        return new THREE.Vector3(X, Y, Z);
    },

    mapEphemerides(data) {
        // ---- helpers ---------------------------------------------------------
        const toJD = (v) =>
            v == null ? null : (v < 2400000.5 ? v + 2400000.5 : v);

        const toNum = (v) =>
            v == null || v === ''
                ? null
                : typeof v === 'number'
                ? v
                : Number(v);

        const isNum = (v) => Number.isFinite(v);

        // Support multiple naming conventions (real vs synthetic)
        const eRaw = toNum(
            data.e ??
            data.eccentricity ??
            data.ecc
        ); // original eccentricity

        let e = eRaw;

        const q = toNum(
            data.q ??
            data.perihelionDistance ??
            data.pericentreDistance
        ); // perihelion distance [AU]

        let a = toNum(data.a); // semi-major axis [AU] (may be null)

        const i = toNum(data.i) ?? toNum(data.incl) ?? null; // deg
        const om = toNum(data.om) ?? toNum(data.node) ?? null; // deg (Ω)
        const w = toNum(data.w) ?? toNum(data.peri) ?? null; // deg (ω)

        const epochJD = toJD(toNum(data.epoch)); // JD
        const tpJD = toJD(toNum(data.tp)); // JD (MJD auto-converted)

        // Small epsilon for hyperbolic to help numeric stability if needed
        const EPS_E = 1e-8;
        if (isNum(e) && e > 1) {
            e = e + EPS_E;
        }

        // If 'a' is missing but q and e exist, derive it:
        //   q = a (1 − e)  =>  a = q / (1 − e)
        if (!isNum(a) && isNum(q) && isNum(e)) {
            const denom = 1 - e;
            a = Math.abs(denom) > 1e-12 ? q / denom : null; // e = 1 → leave null
        }

        // For hyperbolic, enforce the consistent relation a = q/(1−e) if both are set
        if (isNum(e) && e > 1 && isNum(q) && isNum(a)) {
            const expectedA = q / (1 - e);
            if (a > 0) {
                a = expectedA;
            }
        }

        // ---- mean motion n [deg/day] -----------------------------------------
        const K = 0.01720209895; // rad/day
        const mu = K * K; // AU^3/day^2

        const aAbs = isNum(a) ? Math.abs(a) : NaN;

        let n_rad =
            isNum(aAbs) && aAbs > 0 ? Math.sqrt(mu / (aAbs ** 3)) : NaN;

        // Fallback from data.n if present (deg/day)
        if (!isNum(n_rad) && isNum(toNum(data.n))) {
            n_rad = toNum(data.n) * (Math.PI / 180);
        }
        if (!isNum(n_rad)) {
            n_rad = 0;
        }

        const n_deg = n_rad * (180 / Math.PI);

        // ---- mean anomaly at epoch ma [deg] ----------------------------------
        let ma_deg = toNum(data.ma ?? data.m);

        if (
            !isNum(ma_deg) &&
            Number.isFinite(epochJD) &&
            Number.isFinite(tpJD) &&
            isNum(n_deg)
        ) {
            // ma = n * (epoch − tp)
            ma_deg = n_deg * (epochJD - tpJD);
        }

        if (!isNum(ma_deg)) {
            ma_deg = 0;
        }

        // ---- build Spacekit ephemeris ----------------------------------------
        const ephem = new Spacekit.Ephem(
            {
                a: isNum(a) ? a : null,
                e: isNum(e) ? e : null,
                i,
                om,
                w,
                q: isNum(q) ? q : null,
                epoch: epochJD ?? null, // JD
                tp: tpJD ?? null, // JD
                ma: ma_deg, // deg
                n: n_deg, // deg/day
            },
            'deg'
        );

        return ephem;
    },

    createScreenScaleUpdater(mesh, camera, renderer, particleSize) {

        return function () {
                mesh.getWorldPosition(worldPos);
                const distance = camera.position.distanceTo(worldPos);
                const fovInRadians = camera.fov * Math.PI / 180;
                const viewportHeight = renderer.domElement.clientHeight;

                const worldSizePerPixel = 2 * distance * Math.tan(fovInRadians / 2) / viewportHeight;
                const desiredPixelSize = particleSize * 19.5;
                const scale = desiredPixelSize * worldSizePerPixel;

                const safeScale = Math.max(MIN_SCALE, Math.min(scale, MAX_SCALE));
                mesh.scale.set(safeScale, safeScale, safeScale);
        }
    },    

    addSmallObjectsLessEccentric(type, phrase, smallObjects, color) {
        const particleSize = VisualisationService.getSettings().particleSize;
        const selects = VisualisationService.getOrbitVisualisationSelects();
        let optionFromSelects = [];
        if (selects[type]) {
            optionFromSelects =  selects[type].filter(e => e.option == phrase);
        }

        if (!optionFromSelects.length)
        {            
            return;
        }

        color = Number('0x' + optionFromSelects[0].color);        

        const vt = window['vt'];
        for (const [index, object] of smallObjects.entries()) {
            const name = `${type}_${phrase}_${index}`;
            try {
                const ephem = SpacekitService.mapEphemerides(object);
                const smallObject = vt.createObject(name, {
                    hideOrbit: true,
                    particleSize,
                    theme: {
                        color,
                    },
                    ephem,
                    name,
                    mag: object.mag || object.h || null,
                    diam: object.diam || object.size,
                });
                vt.smallObjects.push(smallObject);
            } catch (error) {
                const message = String(error?.message || error || '');
                if (!message.includes('Must define eccentricity "e" in an orbit')) {
                    PopupService.show({
                        component: 'PopupInfo',
                        type: 'error',
                        message: error,
                    });
                }
                continue;
            }
        }
        SpacekitService.updateDisplayLimiters();
        if (vt.subscribedObjects.KeplerParticles__0 && vt.subscribedObjects.KeplerParticles__0.get3jsObjects()[0]) {
            vt.subscribedObjects.KeplerParticles__0.get3jsObjects()[0].frustumCulled = false;
        }
    },

    addSmallObjectsHighlyEccentric(type, phrase, smallObjects, color) {
        const selects = VisualisationService.getOrbitVisualisationSelects();
        let optionFromSelects = [];
        if (selects[type]) {
            optionFromSelects = selects[type].filter(e => e.option == phrase);
        }

        if (!optionFromSelects.length) return;

        color = Number('0x' + optionFromSelects[0].color);
        const vt = window['vt'];

        vt.addObject = function (obj, noUpdate = false) {
            obj.get3jsObjects().map((x) => {
                this.scene.add(x);
            });

            if (!noUpdate) {
                const objId = obj.getId();
                this.subscribedObjects[objId] = obj;
            }
        };

        for (const [index, object] of smallObjects.entries()) {
            const particleSize = VisualisationService.getSettings().particleSize;
            const name = `${type}_${phrase}_${index}`;

            if (vt.spaceObjects.some(obj => obj._id === name) || vt.smallObjects.some(obj => obj._id === name)) {
                continue;
            }

            try {
                const ephem = SpacekitService.mapEphemerides(object);
                let smallObject;

                const geometry = new THREE.SphereGeometry(1, 8, 8);
                geometry.computeBoundingSphere();
                geometry.center(); // make sure it's at (0, 0, 0)

                smallObject = vt.createSphere(name, {
                    name,
                    ephem,
                    diam: 0.00002,
                    mag: object.mag || object.h || null,
                    labelLayer: LayerService.getToolLayers().OBJECT_NAMES,
                    theme: { color },
                    orbitPathSettings: {
                        numberSamplePoints: 200,
                    },
                    geometry,
                });

                const lod = smallObject._object3js?.children?.[0];
                if (lod instanceof THREE.LOD) {
                    lod.levels = [lod.levels[0]];
                    lod.update = function () {};
                }

                const mesh = lod?.children?.[0];
                if (mesh) {
                    mesh.renderOrder = 999;
                    if (!(mesh.material instanceof THREE.MeshBasicMaterial)) {
                        mesh.material?.dispose?.();
                        mesh.material = new THREE.MeshBasicMaterial({ color: new THREE.Color(color) });
                    } else {
                        mesh.material.color.setHex(color);
                        mesh.material.needsUpdate = true;
                    }

                    const camera = vt.getViewer().camera;
                    const renderer = vt.getRenderer(); // or vt.getViewer().renderer

                    mesh.onBeforeRender = SpacekitService.createScreenScaleUpdater(mesh, camera, renderer, particleSize);
                    mesh.frustumCulled = false;
                    // mesh.matrixAutoUpdate = false; // optional if object doesn't move
                }

                if (!smallObject?._object3js) {
                    console.warn(`Invalid object3js for ${name}, skipping.`);
                    continue;
                }

                // Static initial scale fallback
                const scale = particleSize * SPHERE_SCALE_FACTOR;
                smallObject._object3js.scale.set(scale, scale, scale);

                if (smallObject._orbitPath) {
                    vt.getScene().remove(smallObject._orbitPath);
                    smallObject._orbitPath.geometry?.dispose?.();
                    smallObject._orbitPath.material?.dispose?.();
                    smallObject._orbitPath = null;
                }

                if (smallObject._orbit) {
                    smallObject._orbit.generateAndCacheOrbitShape = () => new THREE.Object3D();
                }

                smallObject.update(vt.getJd(), true);
                vt.smallObjects.push(smallObject);
            } catch (error) {
                const message = String(error?.message || error || '');
                if (!message.includes('Must define eccentricity "e" in an orbit')) {
                    PopupService.show({
                        component: 'PopupInfo',
                        type: 'error',
                        message: error,
                    });
                }
                continue;
            }
        }

        SpacekitService.updateDisplayLimiters();

        if (vt.subscribedObjects.KeplerParticles__0 && vt.subscribedObjects.KeplerParticles__0.get3jsObjects()[0]) {
            vt.subscribedObjects.KeplerParticles__0.get3jsObjects()[0].frustumCulled = false;
        }
    },

    addSmallObjects(type, phrase, smallObjectsLessEccentric, smallObjectsHighlyEccentric, color) {

        if (smallObjectsHighlyEccentric && smallObjectsHighlyEccentric.length > 0) {
            //console.log('Adding highly eccentric small objects:', smallObjectsHighlyEccentric.length);
            SpacekitService.addSmallObjectsHighlyEccentric(type, phrase, smallObjectsHighlyEccentric, color);
        }
        
        if (smallObjectsLessEccentric && smallObjectsLessEccentric.length > 0) {
            //console.log('Adding less eccentric small objects:', smallObjectsLessEccentric.length);
            SpacekitService.addSmallObjectsLessEccentric(type, phrase, smallObjectsLessEccentric, color);
        } 
        const currentSize = VisualisationService.getSettings().particleSize;
        SpacekitService.resizeParticles(currentSize);

    },

    getSmallObjectsGroup(group, option = null) {
        if ( group === 'risk_list') {
            group = 'risk';
        }

        let groupId = group;
        if (option) {
            groupId = group + '_' + option;
        }

        const vt = window['vt'];
        const selectedGroup = vt.smallObjects.filter(e => e._id && e._id.indexOf(groupId) === 0);

        return selectedGroup;
    },

    removeSmallObjects(group, option = null) {
        const vt = window['vt'];
        const groupObjects = SpacekitService.getSmallObjectsGroup(group, option);

        for (const groupObject of groupObjects) {
            vt.removeObject(groupObject);
            SpacekitService.hideParticle(groupObject);
            const indexToRemove = vt.smallObjects.indexOf(groupObject);
            vt.smallObjects.splice(indexToRemove, 1);
        }
    },
    
    getObject(designator) {
        const vt = window['vt'];
        const isPlanet = Config.planetsSelectList.some(planet => planet.toLowerCase() === designator);
        return isPlanet ? vt.planets.filter(planet => (planet._id === designator))[0] : vt?.spaceObjects?.filter(e => (e._id == designator || e._id == designator+'_perturbed'))[0];
    },

    getAllObjects() {
        const vt = window['vt'];
        return vt.spaceObjects;
    },

    spaceObjectOptions() {
        return {
            textureUrl: '/static/textures/marker-square.png',
            radius: 0.0025,
            hideOrbit: false,
            theme: {
                color: 0xAAAAAA,
                orbitColor: 0xAAAAAA,
            },
            ecliptic: {
                displayLines: true,
                lineColor: 0x333333,
            },
        }
    },

    toggleGridSize(mainBodyId) {
        const mainBodyType = mainBodyId ? VisualisationService.getMainBodyParameters(mainBodyId).type : null;
        const vt = window['vt'];
        const scene = vt.getScene();
        const settings = VisualisationService.getSettings();

        let divisions = 18;
        if (mainBodyType && mainBodyType === 'rocky') {
            divisions = 36;
        }

        const oldGrid = scene.getObjectByProperty('type', 'GridHelper');
        if (oldGrid) {
            scene.remove(oldGrid);
        }

        const gridHelper = new THREE.GridHelper(120, divisions, 0x666666, 0x333333);
        gridHelper.geometry.rotateX(Math.PI / 2);
        if (!settings.objectsSettings.horizontalGrid.value) {
            gridHelper.visible = false;
        } 
        scene.add(gridHelper);
    },

    removeMarkerByName(markerId) {
        const vt = window['vt'];
        const scene = vt.getScene();
        const marker = scene?.getObjectByName(markerId);

        if (marker) {
            scene.remove(marker);
        } 
    },

    addPerturbedOrbit(ephemerides, designator) {
        SpacekitService.removeMarkerByName(designator);
        //const object = SpacekitService.getObject(designator);
        const objects = VisualisationService.getKeplerianData();
        let object;

        for (const obj of objects) {
            if (obj.catalogueNumber === designator.toString()){
                object = obj;
            }
        }

        if (!object) return;

        const settings = VisualisationService.getSettings();
        const leadDurationYears = settings.objectsSettings.leadDurationYears.value;
        const trailDurationYears = settings.objectsSettings.trailDurationYears.value;
        const numberSamplePoints = Math.min(
            (leadDurationYears + trailDurationYears) * settings.points_density,
            3000
        );
        const {ephemObject, uncTable, closeApproachTable, detectedCloseApproachForFVT_earth, detectedCloseApproachForFVT_mercury, detectedCloseApproachForFVT_venus, detectedCloseApproachForFVT_mars, detectedCloseApproachForFVT_jupiter, detectedCloseApproachForFVT_saturn, detectedCloseApproachForFVT_uranus, detectedCloseApproachForFVT_neptune } = SpacekitService.mapEphemeridesTable(ephemerides);
        const diam = object.diam ? object.diam : UtilsService.estimateDiameter(object.mag);
        const spaceObjectOptions = {...SpacekitService.spaceObjectOptions(), ...{/* eslint-disable-line */
            /*
            name: object ? object._options.name : '',
            diam: object ? object._options.diam : '',
            mag: object ? object._options.mag : '',
            keplerianOrbitPeriod: object ? object._orbit.ephem.attrs.period : '',
            */

            name: object ? object.designator : '',
            diam,
            mag: object ? object.mag : '',
            keplerianOrbitPeriod: object ?  object.orbitPeriod : '',
            

            labelLayer: LayerService.getToolLayers().OBJECT_NAMES,
            ephemTable: ephemObject,
            orbitPathSettings: {
                numberSamplePoints,
                leadDurationYears,
                trailDurationYears,
            },
            uncTable,
            closeApproachTable,
            detectedCloseApproachForFVT_earth,
            detectedCloseApproachForFVT_mercury,
            detectedCloseApproachForFVT_venus,
            detectedCloseApproachForFVT_mars,
            detectedCloseApproachForFVT_jupiter,
            detectedCloseApproachForFVT_saturn,
            detectedCloseApproachForFVT_uranus,
            detectedCloseApproachForFVT_neptune
        }};

        SpacekitService.removeObject(object.catalogueNumber);
        const vt = window['vt'];
        const spaceObject = vt.createSphere(object.catalogueNumber+'_perturbed', spaceObjectOptions);
        spaceObject._object3js.name = object.catalogueNumber.toString();
        vt.spaceObjects.push(spaceObject);
        spaceObject._id = object.catalogueNumber + '_perturbed';
        SpacekitService.addObjectMarker(spaceObject);

        HUDService.createLabel(spaceObject,  null, null,['center', 'top']);
        SpacekitService.turnOffOrbit(spaceObject); 

        let color = UtilsService.hexColor(settings.objectsSettings.keplerianOrbits.color);
        if (spaceObject._useEphemTable) {
            color = UtilsService.hexColor(settings.objectsSettings.perturbedOrbits.color);
        }
        const currentSelectedName = VisualisationService.getSelectedObjectName();
        if (currentSelectedName === object.catalogueNumber.toString())
            SpacekitService.turnOnOrbit(spaceObject, color);

        //const planetOrbitLineThickness  = settings.objectsSettings.objectOrbitLineThickness.value;
        //SpacekitService.updateOrbitLineThickness(planetOrbitLineThickness);
        // Build the orbit line for THIS perturbed object only,
        // using the current "object orbit line thickness" setting.
        const orbitLineThickness = settings.objectsSettings.objectOrbitLineThickness.value;
        SpacekitService.rebuildOrbitPathForObject(spaceObject, orbitLineThickness);
    },

    addSyntheticPerturbedOrbit(ephemerides, syntheticObjectData) {
        const settings = VisualisationService.getSettings();
        const leadDurationYears = settings.objectsSettings.leadDurationYears.value;
        const trailDurationYears = settings.objectsSettings.trailDurationYears.value;
        const sec_to_days = 86400;

        let a_m = syntheticObjectData.a * SpacekitService.AU * Math.pow(10, 3);
        syntheticObjectData.orbitPeriod = 2*Math.PI*Math.sqrt(Math.pow(a_m, 3) / SpacekitService.mu_Sun) / sec_to_days;// compute the orbit period

        const numberSamplePoints = (leadDurationYears + trailDurationYears) * settings.points_density; 
        const {ephemObject, uncTable, closeApproachTable, detectedCloseApproachForFVT_earth, detectedCloseApproachForFVT_mercury, detectedCloseApproachForFVT_venus, detectedCloseApproachForFVT_mars, detectedCloseApproachForFVT_jupiter, detectedCloseApproachForFVT_saturn, detectedCloseApproachForFVT_uranus, detectedCloseApproachForFVT_neptune } = SpacekitService.mapEphemeridesTable(ephemerides);

        const diam = syntheticObjectData.diam ? syntheticObjectData.diam : UtilsService.estimateDiameter(syntheticObjectData.mag);
        const spaceObjectOptions = {...SpacekitService.spaceObjectOptions(), ...{
            name: syntheticObjectData.objectName || '',
            diam,
            mag: syntheticObjectData.mag || '',
            keplerianOrbitPeriod: syntheticObjectData.orbitPeriod || '',
            labelLayer: LayerService.getToolLayers().OBJECT_NAMES,
            ephemTable: ephemObject,
            orbitPathSettings: {
                numberSamplePoints,
                leadDurationYears,
                trailDurationYears,
            },
            uncTable,
            closeApproachTable,
            detectedCloseApproachForFVT_earth,
            detectedCloseApproachForFVT_mercury,
            detectedCloseApproachForFVT_venus,
            detectedCloseApproachForFVT_mars,
            detectedCloseApproachForFVT_jupiter,
            detectedCloseApproachForFVT_saturn,
            detectedCloseApproachForFVT_uranus,
            detectedCloseApproachForFVT_neptune
        }};
        const vt = window['vt'];
        const spaceObject = vt.createSphere(syntheticObjectData.designator+'_perturbed', spaceObjectOptions);
        spaceObject._object3js.name = syntheticObjectData.designator.toString();
        SpacekitService.addObjectMarker(spaceObject);
        vt.spaceObjects.push(spaceObject);
        SpacekitService.turnOffOrbit(spaceObject);
        let color = UtilsService.hexColor(settings.objectsSettings.keplerianOrbits.color);
        if (spaceObject._useEphemTable) {
            color = UtilsService.hexColor(settings.objectsSettings.perturbedOrbits.color);
        }
        const currentSelectedName = VisualisationService.getSelectedObjectName();
        if (currentSelectedName === syntheticObjectData.designator.toString())
            SpacekitService.turnOnOrbit(spaceObject, color);

        const earthObject = vt.planets.filter(e => e._id === 'earth')[0];
        const impactDate = this.checkForSyntheticObjectImpact(spaceObject, earthObject, ephemObject.data[ephemObject.data.length - 1][0]);
        spaceObject.impactDate = impactDate;
        if (impactDate) {
            PopupService.show({
                component: "PopupInfo",
                type: "warning",
                message: Keys.t.trajectoryImpact
                    .replace("{objectName}", syntheticObjectData.objectName)
                    .replace("{impactDate}", impactDate),
            });
        }  

        let isObjectInvisible = false;

        if (impactDate) {
            const simulationDate = moment.utc(TimeFrameService.getSimulationTime()).format("YYYY-MM-DD");
            const simulationTimestamp = new Date(simulationDate).getTime();
            const impactTimestamp = new Date(impactDate).getTime();
            isObjectInvisible = simulationTimestamp > impactTimestamp;
        }

        isObjectInvisible && (spaceObject._object3js.visible = false);

        !isObjectInvisible && HUDService.createLabel(
            spaceObject,
            UtilsService.hexColor(settings.objectsSettings.objectNames.color),
            settings.objectsSettings.objectNamesSize.value
        )
    },

    updatePerturbedOrbit() {
        const vt = window['vt'];
        const perturbedObjects = vt.spaceObjects.filter(o => o._id.includes('_perturbed'));
        const perturbedob = VisualisationService.getIsPerturbed();
        const checked = VisualisationService.getIsChecked();

        for (const spaceObject of perturbedObjects) { 
            const designator = spaceObject._id.replace("_perturbed","");
            if (checked.indexOf(designator) > -1 && perturbedob.includes(designator)) {
                VisualisationService.addOrbitData(designator, true);
            }
        } 
        vt.staticForcedUpdate();
    },

    checkForSyntheticObjectImpact(spaceObject, earthObject, lastDate) {
        const end = TimeFrameService.getTimeFrame().converted.end;
        const xyzObject = spaceObject.getPosition(lastDate);
        const xyzEarth = earthObject.getPosition(lastDate);
        const distanceToEarth = VisualisationService.distanceToBody(xyzObject, xyzEarth, 6) * SpacekitService.AU;
        const impactDetected = lastDate < end && distanceToEarth < SpacekitService.USER_DEFINED_OBJECT_DISTANCE_IMPACT_LIMIT;
        return impactDetected ? UtilsService.dateToStringTechnical(UtilsService.julianToDateObject(lastDate)) : null;
    },

    createObject(object) {
        const vt = window['vt'];
        const ephem = SpacekitService.mapEphemerides(object);
        const diam =
            object.diam ? object.diam : UtilsService.estimateDiameter(object.mag);
        let spaceObjectOptions;

        const isHyperbolicSynthetic =
            object.type === SyntheticObjectsService.OBJECT_TYPES.hyperbolic;
        const isExactParabolicSamplingCase =
            SpacekitService.isExactParabolicSamplingCase(object, {
                isHyperbolicSynthetic,
            });

        if (
            object.object === 'asteroid' &&
            object.e >= 0.9 &&
            !isHyperbolicSynthetic
        ) {
            // High-eccentricity elliptic asteroids: keep dense sampling
            spaceObjectOptions = {
                ...SpacekitService.spaceObjectOptions(),
                ...{
                    name: object.objectName || object.designator,
                    diam,
                    mag: object.mag,
                    labelLayer: LayerService.getToolLayers().OBJECT_NAMES,
                    ephem,
                    orbitPathSettings: {
                        numberSamplePoints: Math.min(2 * object.orbitPeriod, 8000), //2 * object.orbitPeriod,
                        trailDurationYears: 2 * object.orbitPeriod / 365.25,
                        leadDurationYears: 2 * object.orbitPeriod / 365.25,
                    },
                },
            };
        } else {
            // comets, low-ecc objects, and hyperbolic objects
            spaceObjectOptions = {
                ...SpacekitService.spaceObjectOptions(),
                ...{
                    name: object.objectName || object.designator,
                    diam,
                    mag: object.mag,
                    labelLayer: LayerService.getToolLayers().OBJECT_NAMES,
                    ephem,
                    orbitPathSettings: {
                        numberSamplePoints:
                            10 * VisualisationService.getSettings().points_density,
                    },
                },
            };
        }

        const spaceObject = vt.createSphere(
            object.catalogueNumber + '',
            spaceObjectOptions
        );
        spaceObject._object3js.name = object.catalogueNumber.toString();

        // Hybrid strategy for hyperbolic synthetic objects:
        // - Parabolic edge case (e approx 1): keep Spacekit default orbit path.
        // - Any truly hyperbolic case (e > 1): use custom solver + rebuilt path.
        // This avoids the two-branch/asymptote artifact for e just above 1 and
        // still prevents under-sampling for low-q fast-perihelion passes.
        //
        // Tuned against tested domain: q >= 0.01, e > 1.
        const objectEcc = Number(object.e);
        const HYPERBOLIC_EPS = 1e-6;
        const isStrongHyperbolicSynthetic =
            isHyperbolicSynthetic &&
            Number.isFinite(objectEcc) &&
            objectEcc > 1 + HYPERBOLIC_EPS;

        if (isStrongHyperbolicSynthetic) {
            const synParams =
                SyntheticObjectsService.getSyntheticObjectParameters(
                    object.designator,
                    SyntheticObjectsService.OBJECT_TYPES.hyperbolic
                );

            const hyperParams = {
                q: Number(synParams.q),
                e: Number(synParams.e),
                i: Number(synParams.i),    // deg
                om: Number(synParams.om),  // deg
                w: Number(synParams.w),    // deg
                tp: Number(synParams.tp),  // MJD or JD (solver handles it)
                designator:
                    object.catalogueNumber ||
                    object.designator ||
                    object.number ||
                    object.name ||
                    '',
            };

            const originalGetPosition = spaceObject.getPosition
                ? spaceObject.getPosition.bind(spaceObject)
                : null;

            spaceObject.getPosition = function (jd) {
                const pos =
                    SynodicCalculationService.computeHyperbolicKeplerPosition(
                        jd,
                        hyperParams
                    );

                if (
                    pos &&
                    Array.isArray(pos) &&
                    pos.length === 3 &&
                    pos.every(Number.isFinite)
                ) {
                    return pos;
                }

                return originalGetPosition ? originalGetPosition(jd) : pos;
            };

            this.rebuildHyperbolicOrbitPath(spaceObject, vt, hyperParams.tp);
        } else if (isExactParabolicSamplingCase) {
            spaceObject._customOrbitLinePositions =
                SpacekitService.buildOpenOrbitLinePositions(
                    spaceObject,
                    object.tp
                );
        }
        // ----------------------------------------------------

        // Keep the special handling only for 0.9 < e < 1 (elliptical near-parabolic)
        if (ephem.attrs.e > 0.9 && ephem.attrs.e < 1 && ephem.attrs.period) {
            vt.getScene().remove(spaceObject._orbitPath);
            spaceObject.update(vt.getJd(), true);
            spaceObject._orbit.orbitType = 3;
            spaceObject._orbitPath = null;

            const orbitPoints = SpacekitService.getEllipsePoints(spaceObject._orbit);
            spaceObject._orbit.orbitPoints = orbitPoints;
            const cachedOrb =
                spaceObject._orbit.generateAndCacheOrbitShape(orbitPoints);

            spaceObject._orbitPath = cachedOrb;
            vt.getScene().add(cachedOrb);
        }

        vt.spaceObjects.push(spaceObject);

        if (VisualisationService.getTool() === 'ovt') {
            spaceObject._id = object.catalogueNumber;
            SpacekitService.addObjectMarker(spaceObject);
        }

        HUDService.createLabel(spaceObject, null, null, ['center', 'top']);
        SpacekitService.turnOffOrbit(spaceObject);
    },

    getPerihelionDistanceAu(object) {
        const perihelionCandidates = [
            object?.q,
            object?.perihelionDistance,
            object?.pericentreDistance,
        ];

        for (const value of perihelionCandidates) {
            const parsed = Number(value);
            if (Number.isFinite(parsed)) {
                return parsed;
            }
        }

        return null;
    },

    isExactParabolicSamplingCase(object, { isHyperbolicSynthetic = false } = {}) {
        const objectEcc = Number(object?.e ?? object?.eccentricity ?? object?.ecc);
        const perihelionDistance = SpacekitService.getPerihelionDistanceAu(object);
        const HYPERBOLIC_EPS = 1e-6;
        const TARGET_PARABOLIC_Q_MIN = 0.01;
        const TARGET_PARABOLIC_Q_MAX = 0.04;
        const TARGET_PARABOLIC_Q_EPS = 1e-9;

        return (
            isHyperbolicSynthetic &&
            Number.isFinite(objectEcc) &&
            Math.abs(objectEcc - 1) <= HYPERBOLIC_EPS &&
            Number.isFinite(perihelionDistance) &&
            perihelionDistance >= TARGET_PARABOLIC_Q_MIN - TARGET_PARABOLIC_Q_EPS &&
            perihelionDistance <= TARGET_PARABOLIC_Q_MAX + TARGET_PARABOLIC_Q_EPS
        );
    },

    buildOpenOrbitLinePositions(spaceObject, tpRaw = null) {
        try {
            const startDate = new Date(Date.UTC(1950, 0, 1));
            const endDate = new Date();
            endDate.setUTCFullYear(endDate.getUTCFullYear() + 100);

            const jdStart = UtilsService.dateToJulian(startDate);
            const jdEnd = UtilsService.dateToJulian(endDate);
            const sampleJds = SpacekitService.buildHyperbolicSampleJds(
                jdStart,
                jdEnd,
                tpRaw
            );
            const positions = [];

            for (const jd of sampleJds) {
                const point = spaceObject.getPosition(jd);
                if (
                    point &&
                    Array.isArray(point) &&
                    point.length === 3 &&
                    point.every(Number.isFinite)
                ) {
                    positions.push(point[0], point[1], point[2]);
                }
            }

            return positions.length >= 6 ? positions : null;
        } catch (e) {
            console.error('Failed to build open orbit line positions:', e);
            return null;
        }
    },

    getOrbitLinePositions(spaceObject) {
        if (
            spaceObject &&
            Array.isArray(spaceObject._customOrbitLinePositions) &&
            spaceObject._customOrbitLinePositions.length >= 6
        ) {
            return spaceObject._customOrbitLinePositions;
        }

        if (
            spaceObject._orbit &&
            spaceObject._orbit.orbitShape &&
            spaceObject._orbit.orbitShape.geometry &&
            spaceObject._orbit.orbitShape.geometry.attributes &&
            spaceObject._orbit.orbitShape.geometry.attributes.position
        ) {
            return spaceObject._orbit.orbitShape.geometry.attributes.position.array;
        }

        if (
            spaceObject.name === 'averagedEarthOrbit' &&
            spaceObject.geometry &&
            spaceObject.geometry.attributes &&
            spaceObject.geometry.attributes.position
        ) {
            return spaceObject.geometry.attributes.position.array;
        }

        return null;
    },

    normalizeToJulianDay(value) {
        const num = Number(value);
        if (!Number.isFinite(num)) {
            return null;
        }
        // MJD -> JD conversion
        if (num < 1_000_000) {
            return num + 2400000.5;
        }
        return num;
    },

    getHyperbolicSamplingStepDays(distanceToTpDays) {
        if (!Number.isFinite(distanceToTpDays)) {
            return 10;
        }
        // Dense around perihelion time (tp), coarse far away:
        // this keeps narrow low-q hyperbolic arcs smooth without exploding total points.
        if (distanceToTpDays <= 2) return 5 / (24 * 60); // 5 minutes
        if (distanceToTpDays <= 30) return 1 / 24;       // 1 hour
        if (distanceToTpDays <= 365) return 1;           // 1 day
        if (distanceToTpDays <= 3650) return 5;          // 5 days
        return 10;                                       // 10 days
    },

    buildHyperbolicSampleJds(jdStart, jdEnd, tpRaw) {
        const totalDays = jdEnd - jdStart;
        if (!Number.isFinite(totalDays) || totalDays <= 0) {
            return [];
        }

        const tpJD = SpacekitService.normalizeToJulianDay(tpRaw);
        const useAdaptive = Number.isFinite(tpJD);
        const MAX_POINTS = 25000;

        const sampleJds = [];
        let jd = jdStart;
        let guard = 0;

        while (jd <= jdEnd && guard < MAX_POINTS) {
            sampleJds.push(jd);
            const distance = useAdaptive ? Math.abs(jd - tpJD) : Number.POSITIVE_INFINITY;
            jd += SpacekitService.getHyperbolicSamplingStepDays(distance);
            guard += 1;
        }

        sampleJds.push(jdEnd);
        if (useAdaptive && tpJD >= jdStart && tpJD <= jdEnd) {
            sampleJds.push(tpJD);
        }

        sampleJds.sort((a, b) => a - b);
        const deduped = [];
        for (let i = 0; i < sampleJds.length; i++) {
            const current = sampleJds[i];
            if (
                deduped.length === 0 ||
                Math.abs(current - deduped[deduped.length - 1]) > 1e-9
            ) {
                deduped.push(current);
            }
        }
        return deduped;
    },

    rebuildHyperbolicOrbitPath(spaceObject, vt, tpRaw = null) {
        try {
            // start: 1950-01-01
            const startDate = new Date(Date.UTC(1950, 0, 1));
            // end: today + 100 years
            const endDate = new Date();
            endDate.setUTCFullYear(endDate.getUTCFullYear() + 100);

            // convert to Julian dates
            const jdStart = UtilsService.dateToJulian(startDate);
            const jdEnd = UtilsService.dateToJulian(endDate);
            const sampleJds = SpacekitService.buildHyperbolicSampleJds(
                jdStart,
                jdEnd,
                tpRaw
            );

            const orbitPoints = [];

            for (const jd of sampleJds) {
                const p = spaceObject.getPosition(jd);
                if (
                    p &&
                    Array.isArray(p) &&
                    p.length === 3 &&
                    p.every(Number.isFinite)
                ) {
                    orbitPoints.push(new THREE.Vector3(p[0], p[1], p[2]));
                }
            }

            if (orbitPoints.length > 1) {
                vt.getScene().remove(spaceObject._orbitPath);
                spaceObject._orbit.orbitType = 3;
                spaceObject._orbitPath = null;

                spaceObject._orbit.orbitPoints = orbitPoints;
                const cachedOrb =
                    spaceObject._orbit.generateAndCacheOrbitShape(orbitPoints);

                spaceObject._orbitPath = cachedOrb;
                vt.getScene().add(cachedOrb);
                spaceObject.update(vt.getJd(), true);
            }

            spaceObject._customOrbitRange = { jdStart, jdEnd };
        } catch (e) {
            console.error("Failed to rebuild hyperbolic orbit path:", e);
        }
    },

    getEllipsePoints(spaceObjectOrbit){
        const eph = spaceObjectOrbit.ephem;

        const a = eph.get('a');
        const ecc = eph.get('e');
    
        const twoPi = Math.PI * 2;
        const ptsNumber = spaceObjectOrbit.options.orbitPathSettings.numberSamplePoints;
        //const step = twoPi / 90;
        const step = twoPi / ptsNumber;
        const pts = [];
        for (let E = 0; E < twoPi; E += step) {
          const v = 2 * Math.atan(Math.sqrt((1 + ecc) / (1 - ecc)) * Math.tan(E / 2));
          const r = (a * (1 - ecc * ecc)) / (1 + ecc * Math.cos(v));
          const pos = spaceObjectOrbit.vectorToHeliocentric(v, r);

          if (isNaN(pos[0]) || isNaN(pos[1]) || isNaN(pos[2])) {
            console.error(
              'NaN position value - you may have bad or incomplete data in the following ephemeris:',
            );
            console.error(eph);
          }
          pts.push(new THREE.Vector3(pos[0], pos[1], pos[2]));
        }
        pts.push(pts[0]);
        return pts;
    },

    rebuildPlanetOrbitPath(planet) {
        const vt = window['vt'];
        if (!vt || !planet || !planet._orbit || !planet._orbit.ephem) {
            return;
        }

        const orbitPoints = SpacekitService.getEllipsePoints(planet._orbit);
        if (!orbitPoints.length) {
            return;
        }

        if (planet._orbitPath) {
            vt.getScene().remove(planet._orbitPath);
            planet._orbitPath.geometry?.dispose?.();
            planet._orbitPath.material?.dispose?.();
        }

        planet._orbit.orbitPoints = orbitPoints;
        const cachedOrb = planet._orbit.generateAndCacheOrbitShape(orbitPoints);
        planet._orbitPath = cachedOrb;
        vt.getScene().add(cachedOrb);
        planet.update(vt.getJd(), true);
    },

    removeObject(designator) {
        const spaceObject = SpacekitService.getObject(designator);
        if (spaceObject) {
            const vt = window['vt'];
            // Clean up any orbit/ecliptic lines tied to this object to avoid leaks.
            if (spaceObject._orbitPath) {
                try { vt.getScene().remove(spaceObject._orbitPath); } catch (e) { /* ignore */ }
                SpacekitService.disposeLine2(spaceObject._orbitPath);
                spaceObject._orbitPath = null;
            }
            if (spaceObject._eclipticLines) {
                try { vt.getScene().remove(spaceObject._eclipticLines); } catch (e) { /* ignore */ }
                try { spaceObject._eclipticLines.geometry?.dispose?.(); } catch (e) { /* ignore */ }
                try { spaceObject._eclipticLines.material?.dispose?.(); } catch (e) { /* ignore */ }
                spaceObject._eclipticLines = null;
            }
            HUDService.removeLabel(spaceObject);
            vt.removeObject(spaceObject);
            const indexToRemove = vt.spaceObjects.indexOf(spaceObject);
            vt.spaceObjects.splice(indexToRemove, 1);
        }
    },

    getMinCameraDistance(radius) {
        return radius * SpacekitService.AU / 10000;
    },

    setMinCameraDistance(units) {
        const vt = window['vt'];
        VisualisationService.setMinCameraDistance(units);
        vt.getViewer().cameraControls.minDistance = units;
    },

    focusOnObject(designator, flyby = false, setZoom = false) {
        const tool = VisualisationService.getTool();
            if (tool !== 'opt' && tool !== 'scd'){
            const vt = window['vt'];
            const spaceObject = flyby ? vt.subscribedObjects[designator] : SpacekitService.getObject(designator);
            const params = VisualisationService.getMainBodyParameters(designator);
            const loadedState = {...MemoryService.getToolState()};
            let zoom;

            if (loadedState.tool === 'ovt')
                zoom = loadedState.activeZoomOVT;
            else if (loadedState.tool === 'fvt')
                zoom = loadedState.activeZoomFVT;
        
            if (flyby) {
                SpacekitService.setMinCameraDistance(0.15);
            }else{
                SpacekitService.setMinCameraDistance(0.004);
            }
            if (params && params.radius) {
                SpacekitService.setMinCameraDistance(SpacekitService.getMinCameraDistance(params.radius) / 10);
            }
            if (spaceObject){
                vt.getViewer().followObject(spaceObject, [-0.01, -0.01, 0.01]);
            }
            if (flyby) {
                SpacekitService.setZoom(designator && designator !== 'sun' ? 1 : 130);
            } 
            if (zoom && setZoom) {
                SpacekitService.setZoom(zoom);
            }
            setTimeout(() => {
                vt.getViewer().cameraControls.enablePan = true;
                vt.staticForcedUpdate();
            }, 100);
        }
    },

    focusOnCenter(flyby = false, setZoom = false) {
        const tool = VisualisationService.getTool();
        if (tool !== 'opt' && tool !== 'scd'){
            const vt = window['vt'];
            if (!vt || !vt.getViewer()) {
                return;
            }
            const center = flyby ? vt.flybyScene.filter(e => e._id === 'mainbody')[0] :vt.subscribedObjects.sun;
            const isSovtTool = tool === 'sovt';
            const followOffset = isSovtTool ? [0, 0, 0] : [-0.01, -0.01, 0.01];
            if (center){
                vt.getViewer().followObject(center, followOffset);
            }
            const loadedState = {...MemoryService.getToolState()};

            let zoom;

            if (loadedState.tool === 'ovt')
                zoom = loadedState.activeZoomOVT;
            else if (loadedState.tool === 'fvt')
                zoom = loadedState.activeZoomFVT;

            if (flyby) {
                SpacekitService.setZoom(10);
                const params = VisualisationService.getMainBodyParameters(VisualisationService.getSelectedObjectName());
                if (params){
                    SpacekitService.setMinCameraDistance(params.radius * SpacekitService.AU / 10000);
                }
            } 

            if (zoom && setZoom) {
                SpacekitService.setZoom(zoom);
            }
            if (!isSovtTool) {
                setTimeout(() => {
                    const vt = window['vt'];
                    if (!vt) return;

                    const viewer = typeof vt.getViewer === 'function' ? vt.getViewer() : null;

                    // stopFollowingObject() may not exist (or viewer may be null) in some tools/states
                    if (viewer && typeof viewer.stopFollowingObject === 'function') {
                        viewer.stopFollowingObject();
                    }

                    // staticForcedUpdate() may also not exist during transitions
                    if (typeof vt.staticForcedUpdate === 'function') {
                        vt.staticForcedUpdate();
                    }
                }, 100);
            }
        }
    },

    focusOnSovtObject(focusedObject) {
        const vt = window["vt"];
        const isEarthSelected =
            focusedObject ===
            SynodicCalculationService.TRANSFORMED_EARTH_OBJECT_NAME;
        const isPlanetSelected = Config.planetsSelectList.map(planet => `${planet.toLowerCase()}${SynodicCalculationService.TRANSFORMED_SPACE_OBJECT_SUFFIX}`).some(planet => planet === focusedObject);
        const isEarthOnScale = VisualisationService.getSettings().objectsSettings.earthOnScale.value;
        const earthZoomFactor = isEarthOnScale ? SynodicCalculationService.EARTH_ON_SCALE_FACTOR : 1;
        const asteroidZoomFactor = isEarthOnScale ? SynodicCalculationService.ASTEROID_ZOOM_FACTOR : 1;
        const objectsIds = [
            `${focusedObject}${SynodicCalculationService.TRANSFORMED_SPACE_OBJECT_SUFFIX}`,
            `${focusedObject}${SynodicCalculationService.TRANSFORMED_SPACE_OBJECT_SUFFIX_PERTURBED}`,
        ];
        const spaceObject = isPlanetSelected
            ? vt.planets.find((planet) => planet._id === focusedObject)
            : vt.spaceObjects.find((object) => objectsIds.includes(object._id));
        if (!spaceObject) {
            return;
        }
        if (isEarthSelected) {
            SpacekitService.setMinCameraDistance(((6378.1366 / 100000) * earthZoomFactor));
        }
        if (isPlanetSelected && !isEarthSelected) {
            const planetParams = VisualisationService.getMainBodyParameters(focusedObject.replace(SynodicCalculationService.TRANSFORMED_SPACE_OBJECT_SUFFIX, ''));
            SpacekitService.setMinCameraDistance(planetParams && planetParams.radius ? (planetParams.radius * SpacekitService.AU / 100000) : 0.004);
        }
        if (!isPlanetSelected && !isEarthSelected) {
            SpacekitService.setMinCameraDistance(0.004 * asteroidZoomFactor);
        }
        setTimeout(() => {
            vt.getViewer().followObject(spaceObject, [0, 0, 0]);
            vt.getViewer().cameraControls.enablePan = true;
            vt.staticForcedUpdate();
        }, 100);
    },

    turnOffOrbit(spaceObject) {
        if(!spaceObject)
            return

        let color, colorLighter;
        const settings = VisualisationService.getSettings();
        if(spaceObject?._useEphemTable) { //perturbed ligther
            colorLighter = UtilsService.applyOpacityToHexColor(settings.objectsSettings.perturbedOrbits.color, 0.4);                          
        }else{ //keplerian ligther
            colorLighter = UtilsService.applyOpacityToHexColor(settings.objectsSettings.keplerianOrbits.color, 0.4);                        
        }
        color = UtilsService.hexColor(colorLighter);
        spaceObject.getOrbit().options.color = color;
        const orbitColor = new THREE.Color(color);
        this.setOrbitHexColor(spaceObject, orbitColor);
        spaceObject._options.ecliptic.displayLines = false;
        spaceObject._simulation?.getScene()?.remove(spaceObject._eclipticLines);
    },

    setOrbitHexColor(object, hexColor){
        object.getOrbit().getOrbitShape().material.color = new THREE.Color(hexColor);
        const objectsSettings = VisualisationService.getSettings().objectsSettings;
        this.updateOrbitLineThickness(objectsSettings.objectOrbitLineThickness.value);
        this.updateOrbitLineThickness(objectsSettings.planetOrbitLineThickness.value, true);
    },

    turnOnOrbit(spaceObject, color) {
        const settings = VisualisationService.getSettings();
        spaceObject.getOrbit().options.color = Number(color);
        this.setOrbitHexColor(spaceObject, color);
        spaceObject._options.ecliptic.displayLines = settings.objectsSettings.horizontalPlane.value;

        if (settings.objectsSettings.horizontalPlane.value) {
            spaceObject._simulation.getScene().remove(spaceObject._eclipticLines);
            spaceObject._eclipticLines = this.getLinesToEcliptic(spaceObject);
            spaceObject._simulation.getScene().add(spaceObject._eclipticLines);
        } else {
            spaceObject._options.ecliptic.displayLines = false;
            spaceObject._simulation.getScene().remove(spaceObject._eclipticLines);
        } 
    },

    turnOffSynodicOrbit(spaceObject) {
        const objectsSettings = VisualisationService.getSettings().objectsSettings;
        const keplerianOrbitColor = UtilsService.hexColor(UtilsService.applyOpacityToHexColor(objectsSettings.keplerianOrbits.color, 0.4));
        const perturbedOrbitColor = UtilsService.hexColor(UtilsService.applyOpacityToHexColor(objectsSettings.perturbedOrbits.color, 0.4));
        const color = spaceObject._id.toString().endsWith(SynodicCalculationService.TRANSFORMED_SPACE_OBJECT_SUFFIX) 
                ? keplerianOrbitColor : perturbedOrbitColor;
        spaceObject.getOrbit().options.color = color;
        this.setOrbitHexColor(spaceObject, color);
        spaceObject._options.ecliptic.displayLines = false;
        spaceObject._simulation?.getScene()?.remove(spaceObject._eclipticLines);
    },

    highlightObject(designator, activate) {
        const spaceObjects = SpacekitService.getAllObjects();
        const settings = VisualisationService.getSettings();   
        const labelsVisibility = settings.objectsSettings.objectNames.value;

        if (!activate) {
            const activeObject = VisualisationService.getOrbitActiveObject();
            SpacekitService.turnOffOrbit(activeObject)
            VisualisationService.setOrbitActiveObject(null);
        } else {
            for (const spaceObject of spaceObjects) {
                if (spaceObject._id === designator || spaceObject._id === designator+'_perturbed') {
                    let color = UtilsService.hexColor(settings.objectsSettings.keplerianOrbits.color);
                    if (spaceObject._useEphemTable) {
                        color = UtilsService.hexColor(settings.objectsSettings.perturbedOrbits.color);
                    }
                    SpacekitService.turnOnOrbit(spaceObject, color);
                    VisualisationService.setOrbitActiveObject(spaceObject);
                    SpacekitService.toggleObjectsNames(labelsVisibility);
                    SpacekitService.toggleEarthName(true);
                } else {
                    SpacekitService.turnOffOrbit(spaceObject);
                }
            }
        }
    },

    handleSynodicOrbitHiglight() {
        const spaceObjects = SpacekitService.getAllObjects();
        const selectedSynodicSpaceObject = VisualisationService.getSelectedSynodicObjectName();
        const objectIdRegex = new RegExp("^(.*?)" + SynodicCalculationService.TRANSFORMED_SPACE_OBJECT_SUFFIX);
        const objectsSettings = VisualisationService.getSettings().objectsSettings;
        const activeKeplerianOrbitColor = objectsSettings.keplerianOrbits.color;
        const activePerturbedOrbitColor = objectsSettings.perturbedOrbits.color;
        spaceObjects.forEach(object => {
            const matchObjectId = object._id.match(objectIdRegex);
            const tranformedObjectId = matchObjectId ? matchObjectId[1] : object._id;
            if (tranformedObjectId === selectedSynodicSpaceObject) {
                let color = object._id.toString().endsWith(SynodicCalculationService.TRANSFORMED_SPACE_OBJECT_SUFFIX)
                    ? activeKeplerianOrbitColor : activePerturbedOrbitColor;
                SpacekitService.turnOnOrbit(object, color);
            } else {
                SpacekitService.turnOffSynodicOrbit(object);
            }
        })
    },

    play() {
        const vt = window['vt'];
        const tool = VisualisationService.getTool();
        const settings = VisualisationService.getSettings();
        const showArrows = settings.objectsSettings.guidingArrows.value;
        SpacekitService.toggleGuidingArrows(showArrows);
        if (tool === 'ovt') {
            const rotationDirection = this.getSpeed() >= 0 ? 1 : -1; // Determine the rotation direction based on the sign of speed
            const earthSphere = vt.subscribedObjects.earth;
            earthSphere._objectIsRotatable = true;
            earthSphere._options.rotation.enable = true;
            earthSphere._options.rotation.speed = rotationDirection;
        }

        //const currentAsteroid = vt.subscribedObjects.asteroid;
   
        //if (currentAsteroid) {
            //currentAsteroid.startRotation();
        //}

        vt.start();
    },

    pause() {
        const vt = window['vt'];

        const tool = VisualisationService.getTool();
        if (tool === 'ovt'  || tool === 'sovt') {
            const earthSphere = tool === 'ovt' ? vt.subscribedObjects.earth : vt.subscribedObjects.earth_transformed;
            if (earthSphere) {
                earthSphere._objectIsRotatable = false;
                earthSphere._options.rotation.enable = false;
                earthSphere.stopRotation();
            }
        }

        const currentAsteroid = vt.subscribedObjects.asteroid;
        if (currentAsteroid) {
            currentAsteroid.stopRotation();
        }
        vt.stop();
    },

    setDate(date) {
        const tool = VisualisationService.getTool();
        if (tool === 'ovt' || tool === 'fvt' || tool === 'sovt') {
            VisualisationService.setSimulationTime(date);
        }
        const vt = window['vt'];
        vt.setDate(date);
        VisualisationService.updateDistances(date);
    },

    setSpeed(speed) {
        const vt = window['vt'];
        vt.setJdPerSecond(speed);
    },

    getSpeed() {
        const vt = window['vt'];
        return vt.jdPerSecond;
    },    
    toggleHorizontalPlane(boolean) {
        const tool = VisualisationService.getTool();
        let activeObject = VisualisationService.getOrbitActiveObject();
        if (tool === 'sovt') {
            const selectedSynodicObject = VisualisationService.getSelectedSynodicObjectName();
            const visibleSynodicObjectsList = SynodicCalculationService.getVisibleRealSynodicObjectsList();
            const objectIdRegex = new RegExp("^(.*?)" + SynodicCalculationService.TRANSFORMED_SPACE_OBJECT_SUFFIX);
            activeObject = visibleSynodicObjectsList.includes(selectedSynodicObject) ?
                SpacekitService.getAllObjects().find(object => {
                    const matchObjectId = object._id.match(objectIdRegex);
                    const tranformedObjectId = matchObjectId ? matchObjectId[1] : object._id;
                    return tranformedObjectId === selectedSynodicObject
                }) : null;
        }
        if (activeObject && (tool === 'ovt' || tool === 'sovt')) {
            activeObject._eclipticLines.visible = boolean;
            activeObject._options.ecliptic.displayLines = boolean;
            if (boolean) {
                activeObject._simulation.getScene().remove(activeObject._eclipticLines);

                if (tool === 'ovt'){
                    activeObject._eclipticLines = this.getLinesToEcliptic(activeObject);
                }
                else{
                    activeObject._eclipticLines = activeObject._orbit.getLinesToEcliptic();
                }

                activeObject._simulation.getScene().add(activeObject._eclipticLines);
            } else {
                activeObject._simulation.getScene().remove(activeObject._eclipticLines);
            }
        } else {
            const vt = window['vt'];
            for (const object of vt.flybyScene) {
                if (object._id === 'asteroid') {
                    object._eclipticLines.visible = boolean;
                    object._options.ecliptic.displayLines = boolean;
                    if (boolean) {
                        object._simulation.getScene().remove(object._eclipticLines);
                        object._eclipticLines = this.getLinesToEcliptic(object);
                        object._simulation.getScene().add(object._eclipticLines);
                    } else {
                        object._simulation.getScene().remove(object._eclipticLines);
                    }
                    return;
                }
            }
        }       
    },

    toggleObjectsNames(boolean) {
        const vt = window['vt'];
        if (boolean) {
            vt.cameraOrtho.layers.enable(LayerService.getToolLayers().OBJECT_NAMES);
        } else {
            vt.cameraOrtho.layers.disable(LayerService.getToolLayers().OBJECT_NAMES);
        }
    },

    toggleMagnitudeLimitLabels(boolean) {
        const vt = window['vt'];
        if (boolean) {
            vt.cameraOrtho.layers.enable(LayerService.getToolLayers().MAGNITUDE_LABELS);
        } else {
            vt.cameraOrtho.layers.disable(LayerService.getToolLayers().MAGNITUDE_LABELS);
        }
    },

    toggleEarthName(boolean) {
        const vt = window['vt'];
        if (boolean) {
            vt.cameraOrtho.layers.enable(LayerService.getToolLayers().EARTH_NAME);
        } else {
            vt.cameraOrtho.layers.disable(LayerService.getToolLayers().EARTH_NAME);
        }
    },

    toggleClosestApproachPoint(boolean) {
        const vt = window['vt'];
        const closestApproachPoint = vt.getScene().getObjectByName('ClosestApproachPoint');
        //const closestApproachPointLabel = vt.sceneOrtho.getObjectByName('Closest approach point');
        if (closestApproachPoint) {
            closestApproachPoint.visible = boolean;
            //closestApproachPointLabel.visible = boolean;
            if(boolean){
                HUDService.createLabel(closestApproachPoint);
            }
            else{
                HUDService.removeLabel(closestApproachPoint);
            }
        }
    },

    toggleMilkyWay(boolean) {
        const vt = window['vt'];
        if (vt.skybox && vt.skybox.get3jsObjects()[0]) {
            vt.skybox.get3jsObjects()[0].visible = boolean;
        }
    },

    toggleStars(boolean) {
        const vt = window['vt'];
        if (vt.stars && vt.stars.get3jsObjects()[0]) {
            vt.stars.get3jsObjects()[0].visible = boolean;
        }
    },

    updateBackgroundColor(color) {
        const vt = window['vt'];
        const scene = vt.getScene();
        const settings = VisualisationService.getSettings();

        scene.background = new THREE.Color(color );
        scene.background.set( color );

        settings.background = color;
        VisualisationService.setSettings(settings); 
    },

    keepPlanetTexture(){
        const vt = window['vt'];
        const tool = VisualisationService.getTool();
        const inverted = VisualisationService.getInvertColors();

        if (tool === 'ovt') {
            
            if (inverted) {
                for (const planet of vt.planets) {
                    if (!planet) return;
                    let textureUrl = planet._options.textureUrl;
                    let lowName = planet._options.name.toLowerCase();
                    let invLowName = lowName + '_neg';
                    let textureUrlInv = textureUrl.replace(lowName, invLowName);
                    planet._options.textureUrl = textureUrlInv;

                    var textureLoader = new THREE.TextureLoader();
                    textureLoader.load(textureUrlInv, function(newTexture) {
                        planet._obj.children[0].children[0].material.map = newTexture;
                        planet._obj.children[0].children[0].material.needsUpdate = true;
                    });
                }

                for (const planetId in vt.planetMarkers) {
                    const marker = vt.planetMarkers[planetId];
                    if (!marker || !marker.material) continue;

                    const id = planetId.toLowerCase();
                    let invertedColor = planetMarkerColors[id];
                    marker.material.color.setHex(invertedColor);
                    marker.material.needsUpdate = true;
                }
            } 
            else  {
                for (const planet of vt.planets) {
                    if (!planet) return;
                    let textureUrl = planet._options.textureUrl;
                    let lowName = planet._options.name.toLowerCase();
                    let invLowName = lowName + '_neg';
                    let textureUrlInv = textureUrl.replace(invLowName, lowName);
                    planet._options.textureUrl = textureUrlInv;  
                    
                    textureLoader = new THREE.TextureLoader();
                    textureLoader.load(textureUrlInv, function(newTexture) {
                        planet._obj.children[0].children[0].material.map = newTexture;
                        planet._obj.children[0].children[0].material.needsUpdate = true;
                    });    
                }

                for (const planetId in vt.planetMarkers) {
                    const marker = vt.planetMarkers[planetId];
                    if (!marker || !marker.material) continue;

                    const originalColor = planetMarkerColors[planetId.toLowerCase()];
                    marker.material.color.setHex(originalColor);
                    marker.material.needsUpdate = true;
                }
            }
        }
        else if (tool === 'fvt') {
            const mainbody = vt.flybyScene.filter(e => e._id === 'mainbody')[0];
            const moon = vt.flybyScene.filter(e => e._id === 'moon')[0];
            const sun = vt.flybyScene.filter(e => e._id === 'sun')[0];
            const center = mainbody ? mainbody._options.name : '';

            const objects = [moon, mainbody, sun];

            if (inverted) {
                for (const object of objects) {
                    if (!object) return;
                    let textureUrl = object._options.textureUrl;
                    let lowName = object._options.name.toLowerCase();
                    let invLowName = lowName + '_neg';
                    let textureUrlInv = textureUrl.replace(lowName, invLowName);
                    object._options.textureUrl = textureUrlInv;    

                    if (center === 'Saturn') {
                        mainbody.addRings(74270.580913, 140478.924731, '/static/textures/detailed/saturn_rings_neg.png');
                    }       

                    object.init();

                    const settings = VisualisationService.getSettings();
                    const lightEffect = settings.objectsSettings.lightEffect.value;
                    SpacekitService.toggleLightEffect(lightEffect);
                    
                }
            }
            else{
                for (const object of objects) {
                    if (!object) return;
                    let textureUrl = object._options.textureUrl;
                    let lowName = object._options.name.toLowerCase();
                    let invLowName = lowName + '_neg';
                    let textureUrlInv = textureUrl.replace(invLowName, lowName);
                    object._options.textureUrl = textureUrlInv;    

                    if (center === 'saturn') {
                        mainbody.addRings(74270.580913, 140478.924731, '/static/textures/detailed/saturn_rings.png');
                    }        

                    object.init();

                    const settings = VisualisationService.getSettings();
                    const lightEffect = settings.objectsSettings.lightEffect.value;
                    SpacekitService.toggleLightEffect(lightEffect);
                }
            }
        }
    },

    toggleLightEffect(boolean) {
        const vt = window['vt'];
        vt.getScene().traverse(object => {
            const pointLight = vt.getScene().children.filter(e => e.type === 'PointLight')[0];
            if (!pointLight) return;
            const camera = vt.camera.get3jsCamera();
            const controls = vt.camera.get3jsCameraControls();
            const material = object.material;
            const uniforms = material && material.uniforms;
            const shaderMaterial = uniforms && uniforms.lightPos;
            let sourcePosition, targetPosition;

            const propagateLightPosition = (source, target) => {
                target.copy(source);
                controls.addEventListener('change', () => {
                    target.copy(source);
                })
            }

            if (material && (material.type === 'ShaderMaterial' || material.type === 'MeshStandardMaterial')) {
                sourcePosition = boolean ? vt.getLightPosition() : camera.position;
                targetPosition = shaderMaterial ? uniforms.lightPos.value : pointLight.position;
            }

            if (targetPosition && sourcePosition) {
                propagateLightPosition(sourcePosition, targetPosition);
            }
        })
    },

    toggleGuidingArrows(boolean) {
        const vt = window['vt'];
        for (const object of vt.flybyScene) {
            if (object && object.type === 'ArrowHelper') {
                const name = object.name.split('_')[1];
                const targetObject = vt.flybyScene.filter(e => e._id === name)[0];
                if (name === 'asteroid' && targetObject.distance && targetObject.distance.earth <= 0.00015) {
                    object.visible = false;
                } else {
                    object.visible = boolean;
                }  
            } 
        }
    },
    
    toggleHorizontalGrid(boolean) {
        const vt = window['vt'];
        vt.getScene().getObjectByProperty('type', 'GridHelper').visible = boolean;
    },

    toggleUncertaintyRegion(boolean) {
        const vt = window['vt'];
        const uncertaintyRegion = vt.getScene().getObjectByProperty('name', 'UncertaintyRegion');
        if (uncertaintyRegion) {
            uncertaintyRegion.visible = boolean;
        }
    },

    toggleOrbitRange(isVisible) {
        const orbitRangeObjects = SpacekitService.getOrbitRangeObjects();
        orbitRangeObjects.forEach(object => object.visible = isVisible);
    },

    toggleDetectionPolar(isVisible) {
        const detectionPolarObjects = SpacekitService.getDetectionPolarObjects();
        if (detectionPolarObjects.length && isVisible) {
            SynodicCalculationService.adjustDetectionPolarObjectPosition();
            SynodicCalculationService.adjustDetectionPolarMergedPosition();
        }
        detectionPolarObjects.forEach(object => object.visible = isVisible);
        SynodicCalculationService.changeElongationConeAppearance(isVisible);
    },

    changeDetectionPolarTransparency(transparencyValue) {
        const detectionPolarObjects = SpacekitService.getDetectionPolarObjects();
        detectionPolarObjects.forEach(object => object.material.opacity = 1 - transparencyValue);
    },

    changeDetectionPolarColor(color) {
        const detectionPolarObjects = SpacekitService.getDetectionPolarObjects();
        detectionPolarObjects.forEach(object => object.material.color.set(UtilsService.convertHexColorToHexNumber(color)));
    },

    getSceneObjectByName(objectName) {
        const visualisationScene = window['vt'].getScene();
        return visualisationScene?.getObjectByName(objectName);
    },

    updateAsteroidsSizeFactor(value) {
        const tool = VisualisationService.getTool();
        if (tool === 'opt' || tool === 'scd') return;

        const vt = window['vt'];
        const unitsPerAu = tool === 'fvt' ? SpacekitService.VIZ_UNITS : 1;

        if (tool === 'sovt' && vt.spaceObjects.length) {
            for (const object of vt.spaceObjects) {
                SpacekitService.rescaleAsteroid(object, value, unitsPerAu)
            }
        } else {
            const asteroid = vt.flybyScene.filter(e => e._id === 'asteroid')[0];
            SpacekitService.rescaleAsteroid(asteroid, value, unitsPerAu);
        }
    },

    getAsteroidDiameterMeters(asteroid, toolOverride = null) {
        const tool = toolOverride || VisualisationService.getTool();
        const defaultDiameter =
            tool === 'fvt'
                ? SpacekitService.USER_DEFINED_OBJECT_FVT_DEFAULT_DIAMETER
                : SpacekitService.USER_DEFINED_OBJECT_DEFAULT_DIAMETER;

        const rawDiameter = asteroid?._options?.diam;
        const parsedDiameter = Number(rawDiameter);

        if (!Number.isFinite(parsedDiameter) || parsedDiameter <= 0) {
            return defaultDiameter;
        }

        return parsedDiameter;
    },

    rescaleAsteroid(asteroid, value, unitsPerAu) {
        if (!asteroid) return;
        
        const tool = VisualisationService.getTool();
        asteroid.get3jsObjects()[0].scale.set(1, 1, 1);
        const initbox = new THREE.Box3().setFromObject( asteroid.get3jsObjects()[0] );
        const initsize = new THREE.Vector3();
        initbox.getSize(initsize);
        const diameter = SpacekitService.getAsteroidDiameterMeters(asteroid, tool);

        const initdiam = Math.sqrt(initsize.x**2 + initsize.y**2 + initsize.z**2);
        const value_r =  diameter / initdiam * 10**value * unitsPerAu / this.AU / 10**3;
        asteroid.get3jsObjects()[0].scale.set(value_r, value_r, value_r);
    },

    updateMarker(asteroid, marker){  
        
        const vt = window['vt'];
        const now = vt.getDate();
        const nowJD = UtilsService.dateToJulian(now);
        const scale = new THREE.Vector3(); 
        const settings = VisualisationService.getSettings();
        const value = settings.objectsSettings.asteroidsSizeFactor.value;
        const AU = this.AU;
        const camera = vt.getViewer()?.camera;

        if (!camera)
            return

        if (asteroid){
            marker.visible = false;  
            const xyzObject = asteroid.getPosition(nowJD);
            xyzObject.x = xyzObject[0];
            xyzObject.y = xyzObject[1];
            xyzObject.z = xyzObject[2];

            const cameraDistance = scale.subVectors(xyzObject, camera.position).length() / SpacekitService.VIZ_UNITS * AU;
            const asteroidDiameterMeters = SpacekitService.getAsteroidDiameterMeters(asteroid, 'fvt');
            const astDiamKm = asteroidDiameterMeters * 10**value / 10**3;
            const angularSize = Math.atan2(astDiamKm, cameraDistance)*180/Math.PI;

            if (angularSize <= 0.2){
                marker.visible = true;  
            }
            
            }
            marker.position.copy(asteroid.get3jsObjects()[0].position); // Set marker position to object position
            marker.lookAt(camera.position);

            var scaleVector = new THREE.Vector3();    
            var size = 700;
            var scaleFactor = scaleVector.subVectors(marker.position, camera.position).length() / size;

            // Apply the scale factor to the marker to maintain constant size
            marker.scale.set(scaleFactor, scaleFactor, scaleFactor);
    },

    addAsteroidMarker(){
        const vt = window['vt'];
        const asteroid = vt.flybyScene.filter(e => e._id === 'asteroid')[0];
        const scene = vt.getScene();
        const camera = vt.getViewer().camera;
        const settings = VisualisationService.getSettings().objectsSettings;

        const geometry = new THREE.RingGeometry( 7, 10, 100, 1, 0, Math.PI*2); 
        const material = new THREE.MeshBasicMaterial( { 
            color: settings.asteroidOrbit.color, 
            wireframe: false, 
            side: THREE.DoubleSide, 
            transparent: true,
            opacity: .75 
        } );

        const marker = new THREE.Mesh( geometry, material ); 
        marker.name = 'marker_asteroid';
        scene.add(marker);
        vt.flybyScene.push(marker)
        asteroid._label3d[0].visible = true;

        //vt.getRenderer().render(scene, camera);

        // Render loop
        function animate() {
            // Update the marker position
            SpacekitService.updateMarker(asteroid, marker);

            marker.renderOrder = 1000;

            // Render the scene
            if (vt.getRenderer()){
            vt.getRenderer().render(scene, camera);
            vt.getRenderer().render(vt.sceneOrtho, vt.cameraOrtho);
            }

            // Request the next frame
            requestAnimationFrame(animate);
        }

        // Start the animation loop
        animate();
        
    },

    addObjectMarker(object) {
        const vt = window['vt'];
        const scene = vt.getScene();

        // --- Clean up old markers for this object ---
        const fullId = object._id?.toString();
        if (!fullId) {
            return;
        }
        const baseId = fullId?.replace('_perturbed', '');
        const possibleNames = [`marker_${baseId}`, `marker_${baseId}_perturbed`];
        // DEBUG LOG — before cleanup
        //console.groupCollapsed('[Marker Debug] addObjectMarker');
        //console.log('fullId:', fullId);
        //console.log('baseId:', baseId);
        //console.log('cleanup candidates:', possibleNames);

        const markersToRemove = scene.children.filter(obj =>
            possibleNames.includes(obj.name)
        );

        markersToRemove.forEach(marker => {
            //console.log('removing existing marker:', marker.name);
            scene.remove(marker);
        });

        const geometry = new THREE.RingGeometry(3.0, 3.9, 64);

        const material = new THREE.MeshBasicMaterial({
            color: 0xffffff,
            side: THREE.DoubleSide,
            transparent: true,
            opacity: 1.0,
            blending: THREE.NormalBlending,
            depthTest: false,
            depthWrite: false,
            fog: false,
        });

        const marker = new THREE.Mesh(geometry, material);
        marker.name = `marker_${object._id}`;
        marker.visible = false;
        scene.add(marker);
        object._marker = marker;
        //console.log('created marker:', marker.name);
        //const sceneMarkerNames = scene.children
        //    .filter(o => o.name && o.name.startsWith('marker_'))
        //    .map(o => o.name);
        //console.log(`scene markers now (${sceneMarkerNames.length}):`, sceneMarkerNames);
        //console.groupEnd();

        // Start the animation loop once
        if (!vt._objectMarkerAnimationStarted) {
            vt._objectMarkerAnimationStarted = true;
            SpacekitService.animateObjectMarkers();
        }
    },

    removeSovtObjectMarkers(designator) {
        const vt = window['vt'];
        if (!vt) return;
        const scene = vt.getScene();

        // Resolve the two SOVT ids for this designator
        const idKepler = SynodicCalculationService.getTransformedSpaceObjectId(designator, false);   // e.g., "99942_transformed"
        const idPert   = SynodicCalculationService.getTransformedSpaceObjectId(designator, true);    // e.g., "99942_transformed_perturbed"
        const namesToRemove = [`marker_${idKepler}`, `marker_${idPert}`];

        //console.groupCollapsed('[SOVT Marker Remove]', { designator, idKepler, idPert, namesToRemove });

        // 1) Remove scene nodes by name
        //let sceneRemoved = 0;
        for (const name of namesToRemove) {
            const nodes = scene.children.filter(o => o && o.name === name);
            //if (nodes.length) {
            //    console.log(`found ${nodes.length} scene node(s) named "${name}" → removing...`);
            //} else {
            //    console.log(`no scene nodes named "${name}"`);
            //}
            for (const node of nodes) {
                //console.log('  removing scene node:', node.name, node.uuid);
                scene.remove(node);
                node.geometry?.dispose?.();
                node.material?.dispose?.();
                //sceneRemoved++;
            }
        }

        // 2) Clear _marker refs on matching spaceObjects
        //let refsCleared = 0;
        for (const obj of (vt.spaceObjects || [])) {
            if (!obj || !obj._id) continue;
            const isTarget = (obj._id === idKepler || obj._id === idPert);
            if (!isTarget) continue;

            const hasMarker = !!obj._marker;
            //console.log('spaceObject match:', { _id: obj._id, hasMarker });

            if (hasMarker) {
                const m = obj._marker;
                //console.log('  removing attached _marker mesh:', m.name ?? '(unnamed)', m.uuid);
                scene.remove(m);
                m.geometry?.dispose?.();
                m.material?.dispose?.();
                obj._marker = null;
                //refsCleared++;
            }
        }

        // Snapshot remaining marker_* nodes in scene
        //const remaining = scene.children
        //    .filter(o => o?.name && o.name.startsWith('marker_'))
        //    .map(o => o.name);

        //console.log('removed scene nodes:', sceneRemoved, 'cleared _marker refs:', refsCleared);
        //console.log(`remaining marker_* nodes (${remaining.length}):`, remaining);
        //console.groupEnd();
    },

    animateObjectMarkers() {
        const vt = window['vt'];
        const camera = vt.getViewer().camera;
        const settings = VisualisationService.getSettings().objectsSettings;
        const value = settings.asteroidsSizeFactor.value;
        const AU = SpacekitService.AU;

        function update() {
            if (!Array.isArray(vt.spaceObjects)) return;
            for (const object of vt.spaceObjects) {
                const marker = object._marker;
                if (!marker || !object.get3jsObjects) continue;

                const now = vt.getDate();
                const nowJD = UtilsService.dateToJulian(now);
                //const pos = object.getPosition(nowJD);
                //const xyz = new THREE.Vector3(pos[0], pos[1], pos[2]);
                // --- Safe position: try ephemeris, fall back to mesh position ---
                let xyz;
                let pos = null;
                try {
                    pos = object.getPosition(nowJD);
                } catch (e) {
                    pos = null;
                }

                const hasValidPos =
                    Array.isArray(pos) &&
                    pos.length === 3 &&
                    pos.every(Number.isFinite);

                if (hasValidPos) {
                    xyz = new THREE.Vector3(pos[0], pos[1], pos[2]);
                } else {
                    const objects3d = object.get3jsObjects();
                    if (!objects3d || !objects3d[0]) {
                        continue;
                    }
                    // Use the actual rendered position if the ephemeris call fails
                    xyz = objects3d[0].position.clone();
                }

                const cameraDistance = xyz.clone().sub(camera.position).length();

                const rawDiam = object._options?.diam || SpacekitService.USER_DEFINED_OBJECT_DEFAULT_DIAMETER;
                const scaledDiam = Math.log10(rawDiam + 1) * 100;
                const diamKm = scaledDiam * 10 ** value / 1000;
                const angularSize = Math.atan2(diamKm, cameraDistance * AU) * (180 / Math.PI);

                marker.visible = angularSize <= 0.2;
                marker.position.copy(object.get3jsObjects()[0].position);
                marker.lookAt(camera.position);

                const scaleFactor = marker.position.distanceTo(camera.position) / 700;
                marker.scale.set(scaleFactor, scaleFactor, scaleFactor);
            }

            requestAnimationFrame(update);
        }

        update();
    },

    updateUncertaintyDensity(value) {
        const vt = window['vt'];
        const uncertaintyRegions = vt.flybyScene.filter(e => e.name === 'UncertaintyRegion')[0];
        if (uncertaintyRegions) {
            const length = uncertaintyRegions.children.length;
            const middlePoint = Math.round(length / 2);
            if (!value) {
                for (const mesh of uncertaintyRegions.children) {
                    mesh.visible = false;
                }
            } else if (value === length) {
                for (const mesh of uncertaintyRegions.children) {
                    mesh.visible = true;
                }
            } else {
                const visiblePositions = [];
                const delta = length / (value + 1);
                for (let i = 0; i < length; i = i + delta) {
                    const roundIndex = Math.round(i);
                    if (roundIndex && roundIndex !== length) {
                        visiblePositions.push(roundIndex);
                    }
                }
                if (visiblePositions.indexOf(middlePoint) === -1) {
                    visiblePositions[0] = middlePoint;
                }
                for (const [index, mesh] of uncertaintyRegions.children.entries()) {
                    if (visiblePositions.indexOf(index) > -1) {
                        mesh.visible = true;
                    } else {
                        mesh.visible = false;
                    }
                }
            }
        }
    },

    updateUncertaintyFactor(value) {
        const vt = window['vt'];
        const uncertaintyRegions = vt.flybyScene.filter(e => e.name === 'UncertaintyRegion')[0];
        if (uncertaintyRegions) {
            for (const mesh of uncertaintyRegions.children) {
                mesh.scale.set(value, value, value);
            }
        }
    },

    updateUncertaintyColor(value) {
        const vt = window['vt'];
        const uncertaintyRegions = vt.flybyScene.filter(e => e.name === 'UncertaintyRegion')[0];
        if (uncertaintyRegions) {
            for (const mesh of uncertaintyRegions.children) {
                mesh.material.color.setHex(UtilsService.hexColor(value));                
            }
        }
    },

    updateNamesColor(name,color) {
        HUDService.updateLabelsColor(name,color);
    },
    updateNamesSize(name,size) {        
        HUDService.updateLabelsSize(name,size);
    },
    updateOrbitColor(name, color){
        const vt = window['vt'];
        const spaceObject = vt.flybyScene.filter(e => e._id === name)[0];
        const orbitColor = new THREE.Color(UtilsService.hexColor(color));
        if (spaceObject) {
            this.setOrbitHexColor(spaceObject, orbitColor);
        }
        // Update arrow color
        this.updateArrowHelperColor(name, color);
        this.updateMarkerColor(name, color);
    },    
    updateEarthOrbitColor(color){
        const vt = window['vt'];
        const earth = vt.planets.filter(e => e._id === 'earth')[0];
        if (earth) {                    
            const orbitColor = new THREE.Color(UtilsService.hexColor(color));
            this.setOrbitHexColor(earth, orbitColor);
        }
    },

    updateSynodicEarthOrbitColor(color){
        //const averagedEarthOrbitObject = SpacekitService.getSceneObjectByName('averagedEarthOrbit');
        //averagedEarthOrbitObject && (averagedEarthOrbitObject.material.color.set(UtilsService.hexColor(color)));
        const scene = window['vt'].getScene();
        let sceneArray = scene.children;
        for (let i = 0; i < sceneArray.length; i++) {
            if (sceneArray[i].name === 'averagedEarthOrbit') {
                const averagedEarthOrbitObject = sceneArray[i];
                averagedEarthOrbitObject.material.color.set(UtilsService.hexColor(color));
            }
        }
    },

    updatePlanetsOrbitsColor(color){
        const vt = window['vt'];
        const planets = vt.planets.filter(e => e._id !== 'earth');
        const orbitColor = new THREE.Color(UtilsService.hexColor(color));
        for (const planet of planets) {
            this.setOrbitHexColor(planet, orbitColor);        
        }
    }, 
    updateKeplerianOrbitsColor(color){
        const vt = window['vt'];
        if (VisualisationService.getTool() === 'sovt') {
            SpacekitService.handleSynodicOrbitHiglight();
            return;
        }
        const keplerianObjects = vt.spaceObjects.filter(o => !o._id.includes('_perturbed'));
        const orbitColor = new THREE.Color(UtilsService.hexColor(color));
        for (const spaceObject of keplerianObjects) {
            this.setOrbitHexColor(spaceObject, orbitColor);
            const selected = VisualisationService.getSelectedObjectName();
            if (selected && spaceObject._options.name.includes(selected)){
                SpacekitService.turnOnOrbit(spaceObject, orbitColor);
            }else{
                SpacekitService.turnOffOrbit(spaceObject);
            }
        }
        // Update Box line color
        
    }, 
    updatePerturbedOrbitsColor(color){
        const vt = window['vt'];
        if (VisualisationService.getTool() === 'sovt') {
            SpacekitService.handleSynodicOrbitHiglight();
            return;
        }
        const perturbedObjects = vt.spaceObjects.filter(o => o._id.includes('_perturbed'));
        const orbitColor = new THREE.Color(UtilsService.hexColor(color));
        for (const spaceObject of perturbedObjects) {
            this.setOrbitHexColor(spaceObject, orbitColor);
            const selected = VisualisationService.getSelectedObjectName();
            if (selected && spaceObject._options.name.includes(selected)){
                SpacekitService.turnOnOrbit(spaceObject, orbitColor);
            }else{
                SpacekitService.turnOffOrbit(spaceObject);
            }
        }
        // Update Box line color
    },

    updateOrbitLineThickness(value, planet = false) {
        const vt = window['vt'];
        const averagedEarthOrbit = SpacekitService.getSceneObjectByName('averagedEarthOrbit');
        const tool = VisualisationService.getTool();

        let spaceObjects = [];

        if (tool !== 'fvt') {
            if (planet) {
                spaceObjects = [...(vt.planets || [])];

                // Only push if it exists (getSceneObjectByName can return null/undefined)
                if (averagedEarthOrbit) spaceObjects.push(averagedEarthOrbit);
            } else {
                spaceObjects = [...(vt.spaceObjects || [])];
            }
        } else {
            const moon = vt.flybyScene.filter(e => e._id === 'moon')[0];
            const sun = vt.flybyScene.filter(e => e._id === 'sun')[0];
            const mainBody = vt.flybyScene.filter(e => e._id === 'mainBody')[0];
            const asteroid = vt.flybyScene.filter(e => e._id === 'asteroid')[0];

            if (planet) {
                if (moon) spaceObjects.push(moon);
                if (sun) spaceObjects.push(sun);
                if (mainBody) spaceObjects.push(mainBody);
            } else {
                if (asteroid) spaceObjects.push(asteroid);
            }
        }

        for (const spaceObject of spaceObjects) {
            if (!spaceObject) continue;
            const helperId = (spaceObject._id || spaceObject.name || "").toString();
            const isSynodicEarthRotationHelper =
                helperId === SynodicCalculationService.EARTH_FAKE_MOON_NAME ||
                helperId === SynodicCalculationService.EARTH_PLACEHOLDER_NAME;
            if (isSynodicEarthRotationHelper) continue;

            const orbit3js = spaceObject.get3jsObjects ? spaceObject.get3jsObjects()[2] : null;
            const isVisibleOrbitLine =
                (spaceObject.name == 'averagedEarthOrbit' && spaceObject.visible) ||
                !!(
                    (spaceObject._orbitPath && spaceObject._orbitPath.visible !== false) ||
                    (orbit3js && orbit3js.visible !== false) ||
                    (spaceObject.getOrbit && spaceObject.getOrbit() && spaceObject.getOrbit().visible !== false)
                );

            if (!isVisibleOrbitLine) continue;

            // Determine the desired color (this is the *source of truth*)
            let color;
            if (spaceObject._orbit && spaceObject.getOrbit && spaceObject.getOrbit() && spaceObject.getOrbit().getOrbitShape().material) {
                color = spaceObject.getOrbit().getOrbitShape().material.color;
            } else if (spaceObject.name == 'averagedEarthOrbit' && spaceObject.material && spaceObject.material.color) {
                color = spaceObject.material.color;
            }

            if (!color) continue;

            // Skip rebuild ONLY if thickness AND color are already correct on the existing Line2
            const desiredHex = (color && color.getHex) ? color.getHex() : null;
            const currentHex =
                (spaceObject._orbitPath &&
                    spaceObject._orbitPath.material &&
                    spaceObject._orbitPath.material.color &&
                    spaceObject._orbitPath.material.color.getHex)
                    ? spaceObject._orbitPath.material.color.getHex()
                    : null;

            if (
                spaceObject._orbitPath &&
                spaceObject._lastOrbitLineThickness === value &&
                desiredHex !== null &&
                currentHex === desiredHex
            ) {
                continue;
            }

            // Record last applied thickness/color
            spaceObject._lastOrbitLineThickness = value;
            spaceObject._lastOrbitLineColorHex = desiredHex;

            // Build new material
            const newMaterial = new LineMaterial({
                color: color,
                linewidth: 0.0015 * value
            });

            // Get positions
            const positions = SpacekitService.getOrbitLinePositions(spaceObject);

            if (!positions) continue;

            // Build new geometry
            const geometry = new LineGeometry();
            geometry.setPositions(positions);

            // Create the new line
            const line = new Line2(geometry, newMaterial);
            line.computeLineDistances();

            // Remove old orbit path safely
            if (spaceObject._orbitPath && spaceObject._simulation) {
                spaceObject._simulation.getScene().remove(spaceObject._orbitPath);
                SpacekitService.disposeLine2(spaceObject._orbitPath);
                spaceObject._orbitPath = null;
            } else if (spaceObject.name == 'averagedEarthOrbit') {
                // Remove any previous averagedEarthOrbit Line2 from the scene safely (no splice)
                const scene = vt.getScene();
                const toRemove = scene.children.filter(
                    c => c && c.name === 'averagedEarthOrbit' && c.type === 'Line2'
                );
                for (const child of toRemove) {
                    scene.remove(child);
                    SpacekitService.disposeLine2(child);
                }
            }

            // Attach + add to scene
            if (spaceObject._orbit) {
                spaceObject._orbitPath = line;
            }

            if (spaceObject._simulation) {
                spaceObject._simulation.getScene().add(line);
            } else if (spaceObject.name == 'averagedEarthOrbit') {
                line.name = 'averagedEarthOrbit';
                vt.getScene().add(line);
            }
        }
    },

    /**
     * Rebuild the orbit path (Line2) for a single space object.
     * This is a reusable version of the logic inside updateOrbitLineThickness,
     * so we can call it explicitly (e.g. once after creating a perturbed orbit).
     */
    rebuildOrbitPathForObject(spaceObject, lineWidth, { useOptimized = true } = {}) {
        const vt = window['vt'];
        if (!spaceObject) return;

        // Determine the color to use (fallbacks to keep orbit visible)
        let color;
        if (spaceObject._orbit &&
            spaceObject.getOrbit &&
            spaceObject.getOrbit() &&
            spaceObject.getOrbit().getOrbitShape &&
            spaceObject.getOrbit().getOrbitShape().material) {
            color = spaceObject.getOrbit().getOrbitShape().material.color;
        } else if (spaceObject._orbitPath && spaceObject._orbitPath.material && spaceObject._orbitPath.material.color) {
            color = spaceObject._orbitPath.material.color;
        } else if (spaceObject.name === 'averagedEarthOrbit' && spaceObject.material && spaceObject.material.color) {
            color = spaceObject.material.color;
        } else {
            const settings = VisualisationService.getSettings();
            if (spaceObject._useEphemTable) {
                color = new THREE.Color(UtilsService.hexColor(settings.objectsSettings.perturbedOrbits.color));
            } else {
                color = new THREE.Color(UtilsService.hexColor(settings.objectsSettings.keplerianOrbits.color));
            }
        }

        // Get positions for the orbit line
        const positions = SpacekitService.getOrbitLinePositions(spaceObject);
        if (!positions) {
            return;
        }

        //const geometry = new LineGeometry();
        //geometry.setPositions(positions);

        // Kep performance: Put an upper limit on how many vertices are sent to the GPU.
        // Tune MAX_POINTS if needed.
        const MAX_POINTS = 8000; // (8000–20000)
        const positionsForLine = SpacekitService.downsamplePositions(positions, MAX_POINTS);
        const desiredHex = (color && color.getHex) ? color.getHex() : null;
        const desiredLineWidth = 0.0015 * lineWidth;

        if (!useOptimized) {
            const newMaterial = new LineMaterial({
                color: color,
                linewidth: desiredLineWidth
            });
            const geometry = new LineGeometry();
            geometry.setPositions(positions);

            const line = new Line2(geometry, newMaterial);
            line.computeLineDistances();

            if (spaceObject._orbitPath && spaceObject._simulation) {
                spaceObject._simulation.getScene().remove(spaceObject._orbitPath);
                SpacekitService.disposeLine2(spaceObject._orbitPath);
                spaceObject._orbitPath = null;
            }

            if (spaceObject._orbit) {
                spaceObject._orbitPath = line;
            }

            if (spaceObject._simulation) {
                spaceObject._simulation.getScene().add(line);
            }
            return;
        }

        const existingLine = spaceObject._orbitPath;
        const canReuseLine = useOptimized && existingLine && existingLine.isLine2;

        if (canReuseLine) {
            // Reuse existing Line2 to avoid allocations.
            const existingMaterial = existingLine.material;
            const currentHex = (existingMaterial && existingMaterial.color && existingMaterial.color.getHex)
                ? existingMaterial.color.getHex()
                : null;

            if (existingMaterial) {
                if (currentHex !== desiredHex) {
                    existingMaterial.color = color;
                    existingMaterial.needsUpdate = true;
                }
                if (existingMaterial.linewidth !== desiredLineWidth) {
                    existingMaterial.linewidth = desiredLineWidth;
                    existingMaterial.needsUpdate = true;
                }
            }

            if (existingLine.geometry && existingLine.geometry.setPositions) {
                existingLine.geometry.setPositions(positionsForLine);
            } else {
                const geometry = new LineGeometry();
                geometry.setPositions(positionsForLine);
                existingLine.geometry = geometry;
            }

            existingLine.computeLineDistances();
            return;
        }

        let newMaterial = null;
        const existingLineForMaterial = spaceObject._orbitPath;
        if (existingLineForMaterial && existingLineForMaterial.material) {
            newMaterial = existingLineForMaterial.material;
            if (newMaterial.color && newMaterial.color.getHex && newMaterial.color.getHex() !== desiredHex) {
                newMaterial.color = color;
                newMaterial.needsUpdate = true;
            }
            if (newMaterial.linewidth !== desiredLineWidth) {
                newMaterial.linewidth = desiredLineWidth;
                newMaterial.needsUpdate = true;
            }
        } else {
            newMaterial = new LineMaterial({
                color: color,
                linewidth: desiredLineWidth
            });
        }
        let geometry = null;
        const existingLineForGeometry = spaceObject._orbitPath;
        if (existingLineForGeometry && existingLineForGeometry.geometry && existingLineForGeometry.geometry.setPositions) {
            geometry = existingLineForGeometry.geometry;
            geometry.setPositions(positionsForLine);
        } else {
            geometry = new LineGeometry();
            geometry.setPositions(positionsForLine);
        }

        const line = new Line2(geometry, newMaterial);
        line.computeLineDistances();

        // Remove any existing path
        if (spaceObject._orbitPath && spaceObject._simulation) {
            spaceObject._simulation.getScene().remove(spaceObject._orbitPath);
            if (spaceObject._orbitPath.geometry === geometry) {
                spaceObject._orbitPath.geometry = null;
            }
            if (spaceObject._orbitPath.material === newMaterial) {
                spaceObject._orbitPath.material = null;
            }
            SpacekitService.disposeLine2(spaceObject._orbitPath);
            spaceObject._orbitPath = null;
        } else if (spaceObject.name === 'averagedEarthOrbit') {
            const scene = vt.getScene();
            const toRemove = scene.children.filter(
                c => c && c.name === 'averagedEarthOrbit' && c.type === 'Line2'
            );
            for (const child of toRemove) {
                scene.remove(child);
                SpacekitService.disposeLine2(child);
            }
        }

        // Update reference
        if (spaceObject._orbit) {
            spaceObject._orbitPath = line;
        }

        // Add the new line to the scene
        if (spaceObject._simulation) {
            spaceObject._simulation.getScene().add(line);
        } else if (spaceObject.name === 'averagedEarthOrbit') {
            line.name = 'averagedEarthOrbit';
            vt.getScene().add(line);
        }
    },

    /**
     * Downsample a flat positions array [x0,y0,z0,x1,y1,z1,...] to a maximum number of points.
     * Keeps the first and last point.
     * Returns the original array if no downsampling is needed.
     */
    downsamplePositions(positions, maxPoints) {
        if (!positions || !maxPoints) return positions;

        const totalPoints = Math.floor(positions.length / 3);
        if (totalPoints <= maxPoints) return positions;

        // Step size in points
        const step = Math.ceil(totalPoints / maxPoints);

        // Build a new array keeping first/last and every "step" point
        const out = [];

        // Always keep first
        out.push(positions[0], positions[1], positions[2]);

        for (let i = step; i < totalPoints - 1; i += step) {
            const idx = i * 3;
            out.push(positions[idx], positions[idx + 1], positions[idx + 2]);
        }

        // Always keep last
        const last = (totalPoints - 1) * 3;
        out.push(positions[last], positions[last + 1], positions[last + 2]);

        return out;
    },

    disposeLine2(line) {
        if (!line) return;
        try {
            if (line.geometry && line.geometry.dispose) line.geometry.dispose();
            if (line.material) {
                // LineMaterial can be an array in some cases, so be safe
                if (Array.isArray(line.material)) {
                    line.material.forEach(m => m && m.dispose && m.dispose());
                } else if (line.material.dispose) {
                    line.material.dispose();
                }
            }
        } catch (e) {
            // Never break rendering because of dispose issues
            // console.warn('disposeLine2 failed', e);
        }
    },

    toggleGeostationaryOrbit(boolean) {
        const vt = window['vt'];
        const orbit = vt.getScene().getObjectByProperty('name', 'GeostationaryOrbit');
        if (orbit) {
            orbit.visible = boolean;
        }
    },

    toggleAxisGuide(boolean) {
        const vt = window['vt'];
        vt.getScene().getObjectByProperty('type', 'AxesHelper').visible = boolean;
    },   
    toggleSpaceObject(name, boolean) {
        const vt = window['vt'];
        const spaceObject = vt.flybyScene.filter(e => e._id === name)[0];
        if (spaceObject) {
            spaceObject.get3jsObjects()[0].visible = boolean;
            HUDService.updateLabelVisibility(name,boolean);
        }
    },

    toggleOrbit(name, boolean) {
        const vt = window['vt'];
        const spaceObject = vt.flybyScene.filter(e => e._id === name)[0];
        if (spaceObject) {
            spaceObject.get3jsObjects()[2].visible = boolean;
        }
    },
        
    togglePlanets(prop, boolean, earth = false) {
        const vt = window['vt'];
        if (prop === 'name') {
            if (earth) {
                boolean ? vt.cameraOrtho.layers.enable(LayerService.getToolLayers().EARTH_NAME) : vt.cameraOrtho.layers.disable(LayerService.getToolLayers().EARTH_NAME);
            } else {
                boolean ? vt.cameraOrtho.layers.enable(LayerService.getToolLayers().PLANET_NAMES) : vt.cameraOrtho.layers.disable(LayerService.getToolLayers().PLANET_NAMES);
            }
        } else {
            let planets = [];
            if (earth) {
                planets = vt.planets.filter(e => (e._id === 'earth'));
            } else {
                planets = vt.planets.filter(e => (e._id !== 'earth'));
            }
            for (const planet of planets) {
                if (prop === 'orbit' && planet.get3jsObjects()[2]) {
                    planet.get3jsObjects()[2].visible = boolean;
                    planet.getOrbit().visible = boolean;
                }
            }
        }
        if (prop === 'orbit' && earth && VisualisationService.getTool() === 'sovt') {
            SpacekitService.toggleAveragedEarthOrbit(boolean);
        }
    },

    setZoom(zoom) {
        const tool = VisualisationService.getTool();
        const vt = window['vt'];
        VisualisationService.setZoom(zoom);
        if (tool && tool === 'ovt')
            VisualisationService.setZoomOVT(zoom);
        else if (tool && tool === 'fvt'){
            VisualisationService.setZoomFVT(zoom);
        }

        const cameraPosition = vt.getViewer().camera.position;
        const cameraTarget = vt.getViewer().cameraControls.target;
        cameraPosition.sub(cameraTarget).setLength(zoom).add(cameraTarget);
    },

    setOrientation(){
        const tool = VisualisationService.getTool();

        if (tool !== 'opt' && tool !== 'scd'){
            const vt = window['vt'];
            let camera = vt.getViewer().camera;
            const loadedState = {...MemoryService.getToolState()};
            
            if (loadedState && loadedState.OVTcamera && tool === 'ovt'){
                
                camera.position.x = loadedState.OVTcamera.pos.x;
                camera.position.y = loadedState.OVTcamera.pos.y;
                camera.position.z = loadedState.OVTcamera.pos.z;

                camera.rotation.x = loadedState.OVTcamera.rot.x;
                camera.rotation.y = loadedState.OVTcamera.rot.y;
                camera.rotation.z = loadedState.OVTcamera.rot.z;

                camera.quaternion.w = loadedState.OVTcamera.quat.w;
                camera.quaternion.x = loadedState.OVTcamera.quat.x;
                camera.quaternion.y = loadedState.OVTcamera.quat.y;
                camera.quaternion.z = loadedState.OVTcamera.quat.z;

            } else if (loadedState && loadedState.FVTcamera && tool === 'fvt'){

                camera.position.x = loadedState.FVTcamera.pos.x;
                camera.position.y = loadedState.FVTcamera.pos.y;
                camera.position.z = loadedState.FVTcamera.pos.z;

                camera.rotation.x = loadedState.FVTcamera.rot.x;
                camera.rotation.y = loadedState.FVTcamera.rot.y;
                camera.rotation.z = loadedState.FVTcamera.rot.z;

                camera.quaternion.w = loadedState.FVTcamera.quat.w;
                camera.quaternion.x = loadedState.FVTcamera.quat.x;
                camera.quaternion.y = loadedState.FVTcamera.quat.y;
                camera.quaternion.z = loadedState.FVTcamera.quat.z;

            }
        }
        //cameraPosition.sub(cameraTarget).setLength(zoom).add(cameraTarget);
    },

    updateDisplayLimiters() {
        const vt = window['vt'];
        const smallObjects = vt.smallObjects;
        const settings = VisualisationService.getSettings();
        
        const minDiameter = settings.objectsSettings.diameter.value[0];
        const maxDiameter = settings.objectsSettings.diameter.value[1];
        const minMagnitude = settings.objectsSettings.magnitude.value[0];
        const maxMagnitude = settings.objectsSettings.magnitude.value[1];
        
        for (const smallObject of smallObjects) {
            if (smallObject._options.mag && smallObject._options.diam) {
                if (smallObject._options.diam*1 >= minDiameter && smallObject._options.diam*1 <= maxDiameter && smallObject._options.mag*1 >= minMagnitude && smallObject._options.mag*1 <= maxMagnitude) {
                    SpacekitService.showParticle(smallObject);
                } else {
                    SpacekitService.hideParticle(smallObject);
                }
            }
        }
    },

    resizeParticles(size) {
        const vt = window['vt'];
        const smallObjects = vt.smallObjects;
        for (const smallObject of smallObjects) {
            SpacekitService.resizeParticle(smallObject, size);
        }
        VisualisationService.setParticleSize(size);
    },

    updateGroupColor(smallObjectsGroup, color) {
        for (const smallObject of smallObjectsGroup) {
            SpacekitService.updateParticleColor(smallObject, color);
        }
    },

    hideParticle(smallObject) {
        smallObject.hidden = true;
        SpacekitService.setParticleSize(smallObject._context.objects.particles, smallObject._particleIndex, 0, [999999, 999999, 999999]);
    },

    showParticle(smallObject) {
        smallObject.hidden = false;
        SpacekitService.setParticleSize(smallObject._context.objects.particles, smallObject._particleIndex, smallObject.particleSize || smallObject._options.particleSize, [0,0,0]);
    },

    resizeParticle(smallObject, size) {
        smallObject.particleSize = size;
    
        // Particle system-based objects
        if (smallObject._context?.objects?.particles && !smallObject.hidden) {
            SpacekitService.setParticleSize(
                smallObject._context.objects.particles,
                smallObject._particleIndex,
                size,
                [0, 0, 0]
            );
        }

        // Sphere-based objects (e > 0.9)
        if (smallObject._object3js?.scale) {
            const scale = size * SPHERE_SCALE_FACTOR;
            smallObject._object3js.scale.set(scale, scale, scale);
        }
    },

    updateParticleColor(smallObject, hexColor) {
        const numColor = UtilsService.hexColor(hexColor);
        const color = new THREE.Color(numColor);

        // Particle system
        if (smallObject._context?.objects?.particles) {
            SpacekitService.setParticleColor(
                smallObject._context.objects.particles,
                smallObject._particleIndex,
                color
            );
        }
        // Sphere material inside LOD
        const lod = smallObject._object3js?.children?.[0];
        const mesh = lod?.children?.[0];
        if (mesh?.material?.color) {
            mesh.material.color.set(color);
            mesh.material.needsUpdate = true;
        }
    },

    setParticleSize(particles, offset, size, origin) {
        const attributes = particles.attributes;
        attributes.size.set([size], offset);
        attributes.origin.set(origin, offset * 3);

        for (const attributeKey in attributes) {
            attributes[attributeKey].needsUpdate = true;
        }
    },

    setParticleColor(particles, offset, color) {
        const attributes = particles.attributes;
        attributes.fuzzColor.set([color.r, color.g, color.b], offset * 3);
        for (const attributeKey in attributes) {
            attributes[attributeKey].needsUpdate = true;
        }
    },

    setCameraPosition(x, y, z) {
        const timeout = 33;
        setTimeout(() => {
            const vt = window['vt'];
            vt.getViewer().camera.position.set(x, y, z);
            const settings = VisualisationService.getSettings();
            const zoom = settings.zoom.value;
            
            if (z > 0) {
                const controls = vt.getViewer().cameraControls;
                controls.minAzimuthAngle = 0;
                controls.maxAzimuthAngle = 0;
                controls.update();
                setTimeout(() => {
                    controls.minAzimuthAngle = -Infinity;
                    controls.maxAzimuthAngle = Infinity;
                    controls.update();
                }, 50);
            }

            SpacekitService.setZoom(Math.pow(zoom, 2));
        }, timeout);
    },

    createSun(jds, suneph, sundir) {
        const vt = window['vt'];
        const sunData = suneph ? suneph.map(e => [e.x, e.y, e.z]) : [];
        const sunDir = [sundir[0].x, sundir[0].y, sundir[0].z];
        if (!sunData.length) {
            for (let i = 0; i < jds.length; i++) {
                sunData.push(sunDir);
            }
        }
        const sunEphemTable = SpacekitService.mapEphemeridesTableForFlyby(jds, sunData);
        const params = VisualisationService.getMainBodyParameters('sun');
        const rotation = SpacekitService.mapObjectRotation(params);
        const sunOrbitColor = UtilsService.hexColor(VisualisationService.getSettings().objectsSettings.sunOrbit.color);
        const sun = vt.createSphere('sun', {
            textureUrl: '/static/textures/detailed/sun.jpg',
            radius: params.radius,
            name: params.name,
            labelLayer: LayerService.getToolLayers().OBJECT_NAMES,
            rotation,
            ephemTable: sunEphemTable,
            theme: {
                color: SpacekitService.COLOR_SUN,
                orbitColor: sunOrbitColor
            }
        });
        sun._object3js.name = 'sun';

        const sunMidPosition = sun.getPosition(jds[Math.floor(jds.length / 2)]);
        vt.createLight(sunMidPosition);

        vt.flybyScene.push(sun);
        const sunLabelColor = UtilsService.hexColor(VisualisationService.getSettings().objectsSettings.objectNames.color);        
        HUDService.createLabel(sun, sunLabelColor);
    },

    createFlybyScene(jds, center, suneph, mooneph, asteph, shape, name, designator, diam, mag, sundir, ellipsoids, isSyntheticObject = false, flybyTime = null) {
        SpacekitService.createSun(jds, suneph, sundir);
        SpacekitService.createMoon(jds, mooneph);
        SpacekitService.createAsteroidFlyby(jds, asteph, shape, name, designator, suneph, diam, mag, ellipsoids, isSyntheticObject);
        SpacekitService.createMainBody(jds, center, flybyTime);

        const settings = VisualisationService.getSettings();
        const asteroidOrbitVisibility = settings.objectsSettings.asteroidOrbit.value;
        const moonOrbitVisibility = settings.objectsSettings.moonOrbit.value;
        const moonVisibility = settings.objectsSettings.moon.value;
        const sunVisibility = settings.objectsSettings.sun.value;
        const sunOrbitVisibility = settings.objectsSettings.sunOrbit.value;
        const labelsVisibility = settings.objectsSettings.objectNames.value;
        const magnitudeLabelsVisibility = settings.objectsSettings.limitingMagnitudeLegend.value;
        const closestApproachPoint = settings.objectsSettings.closestApproachPoint.value;
        const lightEffect = settings.objectsSettings.lightEffect.value;
        const uncertaintyFactor = settings.objectsSettings.uncertaintyFactor.value;
        const uncertaintyDensity = settings.objectsSettings.uncertaintyDensity.value;
        const horizontalPlane = settings.objectsSettings.horizontalPlane.value;
        const uncertaintyRegion = settings.objectsSettings.uncertaintyRegion.value;
        const planetOrbitLineThickness  = settings.objectsSettings.planetOrbitLineThickness.value;
        const objectOrbitLineThickness  = settings.objectsSettings.objectOrbitLineThickness.value;
        SpacekitService.toggleObjectsNames(labelsVisibility);
        SpacekitService.toggleOrbit("asteroid", asteroidOrbitVisibility);
        SpacekitService.toggleOrbit("moon", moonOrbitVisibility);
        SpacekitService.toggleSpaceObject("moon", moonVisibility);
        SpacekitService.toggleSpaceObject("sun", sunVisibility);
        SpacekitService.toggleOrbit("sun", sunOrbitVisibility);
        SpacekitService.toggleMagnitudeLimitLabels(magnitudeLabelsVisibility);
        SpacekitService.toggleClosestApproachPoint(closestApproachPoint);
        SpacekitService.toggleLightEffect(lightEffect);
        SpacekitService.toggleHorizontalPlane(horizontalPlane);
        SpacekitService.toggleUncertaintyRegion(uncertaintyRegion);
        SpacekitService.updateUncertaintyFactor(uncertaintyFactor);
        SpacekitService.updateUncertaintyDensity(uncertaintyDensity); 
        VisualisationService.waitForFlybyScene().then(() => { 
            SpacekitService.updateOrbitLineThickness(planetOrbitLineThickness, true);
            SpacekitService.updateOrbitLineThickness(objectOrbitLineThickness);
        });

    },

    isObjectInScene(designator) {
        const vt = window['vt'];
        if (!vt) return false;
        vt.flybyScene.forEach(obj => console.log(obj._options?.designator || "No designator"));
        const inFlybyScene = vt.flybyScene.some(obj => obj._options?.designator === designator);
        return inFlybyScene;
    },

    removeFlybyScene() {
        const vt = window['vt'];
        while (vt.flybyScene.length) {
            const toRemove = vt.flybyScene[vt.flybyScene.length - 1];
            if (toRemove._label3d && toRemove._label3d.length && toRemove._label3d.length>1){
                for  (let i = 0; i < toRemove._label3d.length; i++){
                    vt.sceneOrtho.remove(toRemove._label3d[i]);
                }
            } else if (toRemove._label3d) {
                vt.sceneOrtho.remove(toRemove._label3d);
                HUDService.removeLabel(toRemove);
            }
            if (!toRemove.uuid) {
                vt.removeObject(toRemove);
            } else {
                vt.getScene().remove(toRemove);
            }
            vt.flybyScene.splice(vt.flybyScene.length - 1, 1);
        }
        VisualisationService.setFlybyClosestPoint(null);
    },

    createMoon(jds, mooneph) {
        if (!mooneph || !mooneph.length) {
            return;
        }
        const vt = window['vt'];
        const moonData = mooneph.map(e => [e.x, e.y, e.z]);
        const moonEphemTable = SpacekitService.mapEphemeridesTableForFlyby(jds, moonData);
        const params = VisualisationService.getMainBodyParameters('moon');
        const rotation = SpacekitService.mapObjectRotation(params);
        const timespan = jds[jds.length - 1] - jds[0];
        const moonOrbitColor = UtilsService.hexColor(VisualisationService.getSettings().objectsSettings.moonOrbit.color);
        const moon = vt.createSphere('moon', {
            textureUrl: '/static/textures/detailed/moon.jpg',
            radius: params.radius,
            name: params.name,
            labelLayer: LayerService.getToolLayers().OBJECT_NAMES,
            orbitPathSettings: {
                numberSamplePoints: 2 * jds.length,
                trailDurationYears: timespan / 365.25,
                leadDurationYears: timespan / 365.25
            },
            rotation,
            ephemTable: moonEphemTable,
            theme: {
                orbitColor: moonOrbitColor
            },
            ecliptic: {
                lineColor: 0x333333,
                displayLines: false,
            }
        });
        moon._object3js.name = 'moon';

        vt.flybyScene.push(moon);   
        const moonLabelColor = UtilsService.hexColor(VisualisationService.getSettings().objectsSettings.objectNames.color);        
        HUDService.createLabel(moon, moonLabelColor);
    },

    createAsteroidFlyby(jds, asteph, shapes, name, designator, suneph, diam, mag, ellipsoids, isSyntheticObject) {
        const vt = window['vt'];
        const {shape, rotation} = SpacekitService.prepareShapeAndRotation(shapes);
        const asteroidData = asteph.map(e => [e.x, e.y, e.z]);
        const asteroidEphemTable = SpacekitService.mapEphemeridesTableForFlyby(jds, asteroidData);
        const timespan = jds[jds.length - 1] - jds[0] + 1;
        const numberSamplePoints = jds.length > 240 ? 2 * jds.length : 10 * jds.length;
        const closestApproachPoint = SpacekitService.createClosestApproachPoint(asteroidEphemTable.data);
        vt.getScene().add(closestApproachPoint);

        const uncertaintyRegion = !isSyntheticObject ? SpacekitService.createUncertaintyRegion(asteroidEphemTable.data, ellipsoids) : null;
        uncertaintyRegion && vt.getScene().add(uncertaintyRegion);
  
        const asteroidOrbitColor = UtilsService.hexColor(VisualisationService.getSettings().objectsSettings.asteroidOrbit.color);        
        const asteroid = vt.createShape('asteroid', {
            shape,
            rotation,
            name,
            labelLayer: LayerService.getToolLayers().OBJECT_NAMES,
            designator,
            diam,
            mag,
            suneph: !!suneph,
            ephemTable: asteroidEphemTable,
            orbitPathSettings: {
                numberSamplePoints,
                trailDurationYears: timespan / 365.25,
                leadDurationYears: timespan / 365.25
            },
            ecliptic: {
                displayLines: true,
            },
            theme: {
                color: SpacekitService.COLOR_KEPLERIAN_SELECTED,
                orbitColor: asteroidOrbitColor
            }
        });
        asteroid._object3js.name = 'asteroid';

        //asteroid.initRotation();

        const e = asteroid._options.rotation;
        var t = Math.PI;
        var n = THREE.MathUtils.degToRad(e.lambdaDeg || 0);
        var i = THREE.MathUtils.degToRad(e.betaDeg||0);

        asteroid.get3jsObjects()[0].rotateY(-(t/2-i));
        asteroid.get3jsObjects()[0].rotateZ(-n);

        asteroid._options.name = name;

        vt.flybyScene.push(asteroid);
        vt.flybyScene.push(closestApproachPoint);
        uncertaintyRegion && vt.flybyScene.push(uncertaintyRegion);

        setTimeout(() => {     
            SpacekitService.updateAsteroidsSizeFactor(VisualisationService.getSettings().objectsSettings.asteroidsSizeFactor.value);
        }, 1000);
        const asteroidLabelColor = UtilsService.hexColor(VisualisationService.getSettings().objectsSettings.objectNames.color);
        HUDService.createLabel(asteroid, asteroidLabelColor);
        HUDService.createLabel(closestApproachPoint);
    },

    getLinesToEcliptic(spaceObject) {
        const tool = VisualisationService.getTool();
        const orbitObject = spaceObject._orbit;
        if (!orbitObject.orbitPoints) {
          // Generate the orbitPoints cache.
          orbitObject.getOrbitShape();
        }
        // Place a cap on visible lines, for large or highly inclined orbits.
        const points = orbitObject.orbitPoints || [];
        let samplingStep, N;

        let filteredPoints = [];
        const totalPoints = points.length;

        if (tool === 'ovt' || tool === 'sovt'){
 
            const trailDurationYears = spaceObject._options.orbitPathSettings.trailDurationYears;
            const leadDurationYears = spaceObject._options.orbitPathSettings.leadDurationYears;
            const kepOrbitPeriod = spaceObject._options.keplerianOrbitPeriod;
            const partOrbit = kepOrbitPeriod >= 365.25 * (trailDurationYears + leadDurationYears);
    
            if (orbitObject.ephem.attrs && orbitObject.ephem.attrs.e >= 0.9){
                N = 360;
            } else if (orbitObject.ephem.attrs && orbitObject.ephem.attrs.e < 0.9) {
                N = 90;
            }

            if (!orbitObject.ephem.attrs){
                N = 360;
                const jdstart = spaceObject._orbit.ephem.data[0][0];
                const jdstop = spaceObject._orbit.ephem.data[spaceObject._orbit.ephem.data.length-1][0];
                const pertDaysTotal = jdstop - jdstart;
                const incompData = pertDaysTotal < kepOrbitPeriod;
                if(partOrbit && !incompData){
                    const partOrbitValue = 365.25 * (trailDurationYears + leadDurationYears) / kepOrbitPeriod;
                    N = 360 * partOrbitValue;
                } else if (incompData) {
                    const partOrbitValue = totalPoints / (2 * kepOrbitPeriod);
                    N = 360 * partOrbitValue;
                }
            }

            samplingStep = totalPoints / N; // Calculate the sampling step as a float

        } else if (tool === 'fvt'){
            samplingStep = 10;
            N = totalPoints / samplingStep;
        }

        // Select N points with equal spacing
        for (let i = 0; i < N; i++) {
            const index = Math.floor(i * samplingStep) % totalPoints; // Calculate the index based on the float samplingStep
            const vertex = points[index];
            filteredPoints.push(vertex);
            filteredPoints.push(new THREE.Vector3(vertex.x, vertex.y, 0));
        }

        const geometry = new THREE.BufferGeometry().setFromPoints(filteredPoints);
        orbitObject.eclipticDropLines = new THREE.LineSegments(
          geometry,
          new THREE.LineBasicMaterial({
            color: orbitObject.options.eclipticLineColor || 0x333333,
            blending: THREE.AdditiveBlending,
          }),
        );
    
        return orbitObject.eclipticDropLines;
    },

    createClosestApproachPoint(positions) {
        const closestApproachPointMaterial = new THREE.MeshBasicMaterial({ color: SpacekitService.COLOR_CLOSEST_POINT });
        const closestApproachPointGeometry = new THREE.SphereGeometry(0.01, 16, 16);
        const closestApproachPoint = new THREE.Mesh(closestApproachPointGeometry, closestApproachPointMaterial);
        closestApproachPoint.name = 'ClosestApproachPoint';
        closestApproachPoint._id = 'ClosestApproachPoint';

        let closestPosition = [];
        let distance = null;
        for (const position of positions) {
            const currentDistance = VisualisationService.distanceToCenter([position[1], position[2], position[3]], 10);
            if (!distance || currentDistance < distance) {
                distance = currentDistance;
                closestPosition = position;
            }
        }
        VisualisationService.setFlybyClosestPoint(closestPosition[0]);
        closestApproachPoint.position.set(closestPosition[1] * SpacekitService.VIZ_UNITS, closestPosition[2] * SpacekitService.VIZ_UNITS, closestPosition[3] * SpacekitService.VIZ_UNITS);
        closestApproachPoint._options = {
            name: Keys.t.closestApproachPoint,
            labelLayer: LayerService.getToolLayers().OBJECT_NAMES,
        }

        return closestApproachPoint;
    },

    createUncertaintyRegion(positions, ellipsoids) {
        const deg2rad = Math.PI/180;
        const ellipsoidGroup = new THREE.Group();
        ellipsoidGroup.name = 'UncertaintyRegion';
        const ellipsoidMaterial = new THREE.MeshBasicMaterial({ color: SpacekitService.COLOR_SUN, transparent: true, wireframe: true});
        if (ellipsoids && ellipsoids.length) {
            for (const [index, position] of positions.entries()) {
                const ell = ellipsoids[index];
                const semiAxes = [ell['sa_1'], ell['sa_2'], ell['sa_3']];
                const rotationSteps = [ell['rotx'] * deg2rad, ell['roty'] * deg2rad, ell['rotz'] * deg2rad];
                const ellSize = semiAxes.map(e => e * SpacekitService.VIZ_UNITS);
                
                const ellipsoidGeometry = new THREE.SphereGeometry(1, 24, 24);
                ellipsoidGeometry.applyMatrix4(new THREE.Matrix4().makeScale(ellSize[0], ellSize[1], ellSize[2]));

                const ellipsoid = new THREE.Mesh(ellipsoidGeometry, ellipsoidMaterial);
                ellipsoid.position.set(position[1] * SpacekitService.VIZ_UNITS, position[2] * SpacekitService.VIZ_UNITS, position[3] * SpacekitService.VIZ_UNITS);
                ellipsoid.geometry.rotateX(rotationSteps[0]);
                ellipsoid.geometry.rotateY(rotationSteps[1]);
                ellipsoid.geometry.rotateZ(rotationSteps[2]);
                ellipsoidGroup.add(ellipsoid);
            }
        }

        return ellipsoidGroup;
    },

    createMainBody(jds, center, flybyTime = null) {
        const vt = window['vt'];
        const params = VisualisationService.getMainBodyParameters(center);
        const mainbody = vt.createSphere('mainbody', {
            textureUrl: `/static/textures/detailed/${center}.jpg`,
            radius: params.radius,
            name: params.name,
            labelLayer: LayerService.getToolLayers().OBJECT_NAMES,
            atmosphere: {
                enable: params.atmosphere,
                outerSizeRatio: 0.1,
            },
            period: params.period,
            jd0: params.jd0,
            lambda: params.lambda,
        })
        mainbody._object3js.name = 'mainbody';

        if (center === 'saturn') {
            mainbody.addRings(74270.580913, 140478.924731, '/static/textures/detailed/saturn_rings.png');
        }
        vt.flybyScene.push(mainbody);
        HUDService.createLabel(mainbody);
        
        const getostationaryOrbit = SpacekitService.createGeostationaryOrbit(params.geostationaryOrbit);
        vt.getScene().add(getostationaryOrbit);
        vt.flybyScene.push(getostationaryOrbit)

        const lambda = params.lambda * Math.PI/180;
        mainbody.get3jsObjects()[0].rotateZ(lambda);

        let jd = jds[0] + 2400000.5;

        if (flybyTime) {
            const currentFlybyDate = new Date(flybyTime);
            jd = UtilsService.dateToJulian(currentFlybyDate);
        }

        setTimeout(() => {
            VisualisationService.updateRotationAngle(params.jd0, jd, true);
        }, 200);

        const length = params.radius * SpacekitService.VIZ_UNITS * 4;
        const settings = VisualisationService.getSettings().objectsSettings;

        const sunColor = settings.sunOrbit.color;
        const moonColor = settings.moonOrbit.color;
        const asteroidColor = settings.asteroidOrbit.color;       
        SpacekitService.createArrow('sun', jd, length, new THREE.Color(UtilsService.hexColor(sunColor)));
        SpacekitService.createArrow('moon', jd, length, new THREE.Color(UtilsService.hexColor(moonColor)));
        SpacekitService.createArrow('asteroid', jd, length, new THREE.Color(UtilsService.hexColor(asteroidColor)));
        SpacekitService.addAsteroidMarker();

        const showArrows = settings.guidingArrows.value;
        const showOrbit = settings.geostationaryOrbit.value;
        SpacekitService.toggleGuidingArrows(showArrows);
        SpacekitService.toggleGeostationaryOrbit(showOrbit);
        //VisualisationService.setSelectedObjectName(center);
        SpacekitService.setMinCameraDistance(SpacekitService.getMinCameraDistance(params.radius));
    },

    prepareShapeAndRotation(shapes) {
        const shape = {
            shapeUrl: '/static/shapes/generic_shape.obj',
            modelSource: 'generic',
        }

        const rotation = {
            lambdaDeg: 251,
            betaDeg: -63,
            period: 3.755067,
            yorp: 1.9e-8,
            phi0: 0,
            jd0: 2443568.0,
        }

        if (shapes.asteroidsShapes && shapes.asteroidsShapes.length && shapes.asteroidsShapes[0].models && shapes.asteroidsShapes[0].models.length) {
            const model = shapes.asteroidsShapes[0].models.filter(e => e.shape !== '')[0];
            if (model) {
                shape.shapeUrl = '/shapes' + model.shape;
                shape.modelSource = model.source;
            }

            const rotationParams = shapes.asteroidsShapes[0].models.filter(e => e.lambdaDeg !== 'N/A')[0];
            if (rotationParams) {
                for (const key in rotationParams) {
                    if (key === 'shape' || key === 'source') continue;
                    rotation[key] = rotationParams[key];
                }
            }
        }

        return {
            shape,
            rotation
        }
    },

    mapObjectRotation(params) {
        return {
            enable: true,
            speed: 1 / params.period
        }
    },

    createArrow(targetName, jd, length, hex) {
        const vt = window['vt'];
        const object = vt.flybyScene.filter(e => e._id && e._id === targetName)[0];
        if (object) {
            const targetPosition = object.getPosition(jd);
            const target = new THREE.Vector3(targetPosition[0], targetPosition[1], targetPosition[2]).normalize();
            const origin = new THREE.Vector3(0, 0, 0);
            const arrow = new THREE.ArrowHelper(target, origin, length, hex);
            arrow.name = 'arrow_' + targetName;
            vt.getScene().add(arrow);
            vt.flybyScene.push(arrow);
        }
    },
    updateMarkerColor(name, color){
        const markerName = 'marker_' + name;
        const vt = window['vt'];
        const marker = vt.flybyScene.filter(e => e.name === markerName)[0]; 
        const newMaterial = new THREE.MeshBasicMaterial({ color: color });        
        if (marker) {
            marker.material = newMaterial;
        }          
    },
    updateArrowHelperColor(name, color){
        const arrowName = 'arrow_' + name;
        const vt = window['vt'];
        const arrow = vt.flybyScene.filter(e => e.name === arrowName)[0];        
        if (arrow) {
            arrow.setColor(color);
        }          
    },

    updateArrows(jd) {
        const settings = VisualisationService.getSettings();
        const vt = window['vt'];
        for (const object of vt.flybyScene) {
            if (object && object.type === 'ArrowHelper') {
                const name = object.name.split('_')[1];
                const targetObject = vt.flybyScene.filter(e => e._id === name)[0];
                if (name === 'asteroid' && targetObject.distance && targetObject.distance.earth <= 0.00015) {
                    object.visible = false;
                } else {
                    object.visible = settings.objectsSettings.guidingArrows.value;
                }  
                if (object.visible) {
                    const targetPosition = targetObject.getPosition(jd);
                    const target = new THREE.Vector3(targetPosition[0], targetPosition[1], targetPosition[2]).normalize();
                    object.setDirection(target);
                }
            }
        }
    },

    enableSovtToolFeatures() {
        const settings = VisualisationService.getSettings().objectsSettings;
        const isMagnitudeLabelVisible = settings.limitingMagnitudeLegend.value;
        const isEarthNameVisible = settings.earthName.value;
        const isObjectsNamesVisible = settings.objectNames.value;
        const planetOrbitLineThickness  = settings.planetOrbitLineThickness.value;
        SpacekitService.enableSovtLightEffect();
        SpacekitService.setZAxisView();
        SpacekitService.enableEclipticGrid();
        SpacekitService.createAveragedEarthOrbit();
        SpacekitService.toggleEarthName(isEarthNameVisible);
        SpacekitService.toggleObjectsNames(isObjectsNamesVisible);
        SpacekitService.updateOrbitLineThickness(planetOrbitLineThickness)
        SpacekitService.toggleMagnitudeLimitLabels(isMagnitudeLabelVisible);
    },

    enableSovtLightEffect() {
        const vt = window['vt'];
        vt.createLight();
    },

    setZAxisView() {
        VisualisationService.viewAxes('z');
    },

    enableEclipticGrid() {
        VisualisationService.updateSetting({
            group: 'objectsSettings',
            change: {
                option: 'horizontalGrid',
                property: 'value',
                value: true,
            }
        });  
    },

    createAveragedEarthOrbit() {
        const scene = window['vt'].getScene();
        const settings = VisualisationService.getSettings();
        const isEarthOrbitVisible = settings.objectsSettings.earthOrbit.value;
        const averagedEarthOribtPoints = SynodicCalculationService.calculateAveragedEarthOrbitPoints().map(([x, y, z]) => new THREE.Vector3(x, y, z));
        const averagedEarthOrbitMaterial = new THREE.LineBasicMaterial({ color: UtilsService.hexColor(settings.objectsSettings.synodicEarthOrbit.color) });
        const averagedEarthOrbitGeometry = new THREE.BufferGeometry().setFromPoints(averagedEarthOribtPoints);
        const averagedEarthOrbitObject = new THREE.Line(averagedEarthOrbitGeometry, averagedEarthOrbitMaterial);
        averagedEarthOrbitObject.visible = isEarthOrbitVisible;
        averagedEarthOrbitObject.name = 'averagedEarthOrbit';
        scene.add(averagedEarthOrbitObject);
    },

    toggleAveragedEarthOrbit(isVisible) {
        const scene = window['vt'].getScene();
        //const averagedEarthOrbitObject = SpacekitService.getSceneObjectByName('averagedEarthOrbit');
        //averagedEarthOrbitObject && (averagedEarthOrbitObject.visible = isVisible);
        let sceneArray = scene.children;
        for (let i = 0; i < sceneArray.length; i++) {
            if (sceneArray[i].name === 'averagedEarthOrbit') {
                const averagedEarthOrbitObject = sceneArray[i];
                averagedEarthOrbitObject.visible = isVisible;
            }
        }
    },

    getSpaceObjectData(ephemData) {
        const vt = window["vt"];
        if (!vt || !vt.simulationElt) {
            return null;
        }
        const diam = ephemData.diam ? ephemData.diam : UtilsService.estimateDiameter(ephemData.mag);
        const ephem = SpacekitService.mapEphemerides(ephemData);
        const spaceObjectOptions = {
            ...SpacekitService.spaceObjectOptions(),
            ...{
                name: ephemData.designator,
                diam,
                mag: ephemData.mag,
                labelLayer: LayerService.getToolLayers().OBJECT_NAMES,
                ephem,
            },
        };
        const spaceObject = vt.createSphere(
            ephemData.catalogueNumber ? ephemData.catalogueNumber.toString() : ephemData.designator.toString(),
            spaceObjectOptions
        );
        vt.removeObject(spaceObject);

        // workaround for objects with e > 0.9 and period
        // should be removed after spacekit.js library upgrade
        if (ephem.attrs.e > 0.9 && ephem.attrs.period) {
            spaceObject._orbit.orbitType = 3;
            spaceObject._orbitPath = null;
            spaceObject.update(vt.getJd(), true);
            vt.removeObject(spaceObject)
        }

        return spaceObject;
    },

    addOrbitRange(perihelionCircumferenceData, aphelionCircumferenceData) {
        const settings = VisualisationService.getSettings();
        const isOrbitRangeVisible = settings.objectsSettings.orbitRange.value;
        const orbitRangeObjectsData = [
            {pointsData: perihelionCircumferenceData, name: 'perihelionOrbit'},
            {pointsData: aphelionCircumferenceData, name: 'aphelionOrbit'}
        ];
        orbitRangeObjectsData.forEach(({pointsData, name}) => SpacekitService.createOrbitRangeObject(pointsData, isOrbitRangeVisible, name));
    },

    createOrbitRangeObject(pointsData, isOrbitRangeVisible, name) {
        const scene = window['vt'].getScene();
        const orbitRangeMaterial = SpacekitService.getOrbitRangeMaterial();
        const orbitRangeObjectLimitPoints = [];
        pointsData.forEach(([x, y]) => orbitRangeObjectLimitPoints.push(new THREE.Vector2(x, y)));
        const orbitRangeObjectGeometry = new THREE.LatheGeometry(orbitRangeObjectLimitPoints, 64);
        const orbitRangeObject = new THREE.Mesh(orbitRangeObjectGeometry, orbitRangeMaterial);
        orbitRangeObject.name = name;
        orbitRangeObject.visible = isOrbitRangeVisible;
        scene.add(orbitRangeObject);
        orbitRangeObject.geometry.rotateX(90 * Math.PI / 180);
    },

    getOrbitRangeMaterial() {
        const orbitRangeColor = VisualisationService.getOrbitRangeColor();
        return new THREE.MeshStandardMaterial({
            color: orbitRangeColor,
            side: THREE.DoubleSide,
        });
    },

    removeOrbitRangeObject() {
        const vt = window['vt'];
        const scene = vt.getScene();
        const orbitRangeObjects = SpacekitService.getOrbitRangeObjects();
        orbitRangeObjects.forEach(object => scene.remove(object));
    },

    getOrbitRangeObjects() {
        const orbitRangeNames = ['perihelionOrbit', 'aphelionOrbit'];
        return orbitRangeNames.map(name => SpacekitService.getSceneObjectByName(name)).filter(orbitRangeObject => orbitRangeObject);
    },

    calculateDetectionPolarObject(objectData) {
        SpacekitService.removeDetectionPolarObjects();
        const detectionPolarCoordinates = SynodicCalculationService.getDetectionPolarPoints();
        const isMergedDetectionPolar = detectionPolarCoordinates.some(([x, y]) => x === null && y === null);
        if (!isMergedDetectionPolar) {
            SpacekitService.addDetectionPolarObjects(detectionPolarCoordinates, objectData);
            return;
        }

        const isDetectionPolarVisible = VisualisationService.getSettings().objectsSettings.detectionPolar.value;
        const {inside, outside} = SpacekitService.getMergedDetectionPolarVectors();       
        SpacekitService.createDetectionPolarObject(inside, isDetectionPolarVisible, 'detectionPolarMergedInside', objectData.magnitudeLimit, -90);
        SpacekitService.createDetectionPolarObject(outside, isDetectionPolarVisible, 'detectionPolarMergedOutside', objectData.magnitudeLimit, -90);
        SynodicCalculationService.changeElongationConeAppearance(isDetectionPolarVisible);
    },

    updateMergedDetectionPolar() {
        const {inside, outside} = SpacekitService.getMergedDetectionPolarVectors();
        SpacekitService.updateMergedDetectionPolarPosition('detectionPolarMergedInside', inside);
        SpacekitService.updateMergedDetectionPolarPosition('detectionPolarMergedOutside', outside);
    },

    updateMergedDetectionPolarPosition(name, vectors) {
        const existingMergedDetectionPolarObject = SpacekitService.getSceneObjectByName(name);
        existingMergedDetectionPolarObject.geometry.dispose();
        existingMergedDetectionPolarObject.geometry = new THREE.LatheGeometry(vectors, 64);
        existingMergedDetectionPolarObject.geometry.rotateZ(-90 * Math.PI/180)
    },

    getMergedDetectionPolarVectors() {
        const detectionPolarCoordinates = SynodicCalculationService.getDetectionPolarPoints();
        const [xEarthPosition] = SynodicCalculationService.getTransformedEarthObjectPosition();
        const xDifference = 1 - xEarthPosition;
        const detectionPolarPoints = detectionPolarCoordinates.map(([x, y]) => [x, xDifference >= 0 ? y - xDifference : y + -1 * xDifference]);
        const detectionPolarVectors = SpacekitService.setDetectionPolarPointsList(detectionPolarPoints);
        const detectionPolarNegativePoints = detectionPolarCoordinates.map(([x, y]) => [x, 1 - y]);
        const detectionPolarNegativeVectors = SpacekitService.setDetectionPolarPointsList(detectionPolarNegativePoints);
        const detectionPolarOnlyUpperVectors = SpacekitService.getFilteredDetectionPolarsVectors(detectionPolarVectors);
        const detectionPolarOnlyDownVectors = SpacekitService.getFilteredDetectionPolarsVectors(detectionPolarVectors.reverse());
        const detectionPolarNegativeOnlyUpperVectors = SpacekitService.getFilteredDetectionPolarsVectors(detectionPolarNegativeVectors);
        const detectionPolarNegativeOnlyDownVectors = SpacekitService.getFilteredDetectionPolarsVectors(detectionPolarNegativeVectors.reverse());
        const mergedVectorsInside = [...detectionPolarOnlyDownVectors, ...detectionPolarNegativeOnlyDownVectors.reverse()];
        const mergedVectorsOutside = [...detectionPolarOnlyUpperVectors, ...detectionPolarNegativeOnlyUpperVectors.reverse()];
        return {
            inside: mergedVectorsInside,
            outside: mergedVectorsOutside,
        }
    },

    getFilteredDetectionPolarsVectors(vectorsList) {
        const filteredVectors = [];
        for (const point of vectorsList) {
            if (point.x === null) {
                break;
            }
            filteredVectors.push(point);
        }
        return filteredVectors;
    },

    addDetectionPolarObjects(detectionPolarCoordinates, objectData) {
        const settings = VisualisationService.getSettings();
        const isDetectionPolarVisible = settings.objectsSettings.detectionPolar.value;
        const detectionPolarPointsList = SpacekitService.setDetectionPolarPointsList(detectionPolarCoordinates);
        const detectionPolarObjectsData = [
            {zRotationAngle: -90, xTransform: 0, name: 'detectionPolar'}, 
            {zRotationAngle: 90, xTransform: 1, name: 'detectionPolarNegative'}
        ];
        detectionPolarObjectsData.forEach(({zRotationAngle, xTransform, name}) => 
            SpacekitService.createDetectionPolarObject(name === 'detectionPolar' ? detectionPolarPointsList.reverse() : detectionPolarPointsList, 
                isDetectionPolarVisible, 
                name, 
                objectData.magnitudeLimit,
                zRotationAngle, 
                xTransform
            ));
        isDetectionPolarVisible && SynodicCalculationService.adjustDetectionPolarObjectPosition();
        SynodicCalculationService.changeElongationConeAppearance(isDetectionPolarVisible);
    },

    createDetectionPolarObject(detectionPolarPointsList, isDetectionPolarVisible, name,  magnitudeLimit, zRotationAngle = 0, xTransform = 0) {
        const scene = window['vt'].getScene();
        const detectionPolarGeometry = new THREE.LatheGeometry(detectionPolarPointsList, 64);
        const detectionPolarMaterial = SpacekitService.getDetectionPolarMaterial();
        const detectionPolar = new THREE.Mesh(detectionPolarGeometry, detectionPolarMaterial);
        detectionPolar.name = name;
        detectionPolar.visible = isDetectionPolarVisible;
        detectionPolar.renderOrder = 1;
        detectionPolar.magnitudeLimit = magnitudeLimit;
        scene.add(detectionPolar);
        zRotationAngle && detectionPolar.geometry.rotateZ(zRotationAngle * Math.PI/180);
        xTransform && (detectionPolar.position.x = xTransform);    
        if (detectionPolar.name == "detectionPolarNegative" || detectionPolar.name == "detectionPolarMergedInside")
            HUDService.createPolarLabel(detectionPolar,  null, null, ['center', 'top']);
    },

    setDetectionPolarPointsList(detectionPolarPointsData) {
        return detectionPolarPointsData.map(([x1, y1]) => new THREE.Vector2(x1, y1));
    },

    getDetectionPolarMaterial() {
        const detectionPolarColor = VisualisationService.getDetectionPolarColor();
        const detectionPolarOpacity = 1 - VisualisationService.getDetectionPolarTransparency();
        return new THREE.MeshStandardMaterial({
            color: detectionPolarColor,
            side: THREE.DoubleSide,
            transparent: true,
            opacity: detectionPolarOpacity,
        });
    },

    removeDetectionPolarObjects() {
        SynodicCalculationService.changeElongationConeAppearance();
        const detectionPolarObjects = SpacekitService.getDetectionPolarObjects();
        if (!detectionPolarObjects.length) {
            return;
        }
        const scene = window['vt'].getScene();
        detectionPolarObjects.forEach(object =>{HUDService.removePolarLabel(object);scene.remove(object);});
    },

    getDetectionPolarObjects() {
        const detectionPolarNames = ['detectionPolar', 'detectionPolarNegative', 'detectionPolarMergedOutside', 'detectionPolarMergedInside'];
        return detectionPolarNames.map(name => SpacekitService.getSceneObjectByName(name)).filter(detectionPolarObject => detectionPolarObject);
    },

    createGeostationaryOrbit(radius) {
        const points = new THREE.Path().absarc(0, 0, radius * SpacekitService.VIZ_UNITS, 0, Math.PI * 2).getPoints(90);
        const geometry = new THREE.BufferGeometry().setFromPoints(points);
        const material = new THREE.LineBasicMaterial({ color: SpacekitService.COLOR_CLOSEST_POINT });

        const orbit = new THREE.LineLoop(geometry, material);
        orbit.name = 'GeostationaryOrbit';

        return orbit;
    },

    addRingShape(options, segments) {
        segments = segments || 120;
      
        const shape = new THREE.Shape();
        shape.arc(0, 0, options.outerRadius, options.startAngle, options.endAngle);
        const points = shape.getPoints(segments);
        points.push(new THREE.Vector2(0, 0));
        const shape2 = new THREE.Shape(points);
      
        const shape3 = new THREE.Shape();
        shape3.arc(0, 0, options.innerRadius, options.startAngle, options.endAngle);
        const points2 = shape3.getPoints(segments);
        points2.push(new THREE.Vector2(0, 0));
        const shape4 = new THREE.Shape(points2);
        shape2.holes.push(shape4);
      
        const geometry = new THREE.ShapeGeometry(shape2, 64);
        const material = new THREE.MeshBasicMaterial({
          color: options.color,
          transparent: options.transparent,
          opacity: options.opacity,
          side: options.side
        });

        return new THREE.Mesh(geometry, material);
      },


      init(tool) {
        const vt = SpacekitService.buildSimulation(tool);
        if (vt){
            vt.spaceObjects = [];
            vt.smallObjects = [];
            vt.stop();
            window['vt'] = vt;
        }
        HUDService.initOrthographicInterface();
        HUDService.initInvertColorsShader();
        HUDService.setAnimationLoop();
        SpacekitService.buildBackgroundObjects(tool);
        SpacekitService.registerStarsDevicePixelRatioListener();
     },

      destroy() {
        let vt = window['vt'];
        SpacekitService.unregisterStarsDevicePixelRatioListener();
        vt.renderEnabled = false;

        const requestId = requestAnimationFrame(vt.animate);
        cancelAnimationFrame(requestId);

        const container = vt.getSimulationElement();
        while (container.lastChild)
            container.removeChild(container.lastChild);

        vt.getContext().objects.composer.dispose();
        vt.getContext().objects.renderer.dispose();
        vt.getContext().objects.scene.dispose;

        for (const el in vt) {
            if (el !== 'initialRenderComplete')
                vt[el] = null;  
        }

        vt = null;
    },
}

export default SpacekitService;

